import useLiveUserUiPreference from "../hooks/useLiveUserUiPreference.js";
import { captureOrdersScroll, restoreOrdersScroll } from "../utils/consolidationOrderNavigation.js";
import React from "react";
import { getProductChoiceError } from "../utils/productCatalog.js";
import { LoaderCircle, Moon, Sun } from "lucide-react";
import DataManagerOrdersBrowserDialog from "./DataManagerOrdersBrowserDialog.jsx";
import DataManagerOrdersConfirmationDialogs from "./DataManagerOrdersConfirmationDialogs.jsx";
import DataManagerOrdersCreatePanel from "./DataManagerOrdersCreatePanel.jsx";
import { BillToCompaniesContext } from "./BillToSelect.jsx";
import DataManagerOrdersPdfDialog from "./DataManagerOrdersPdfDialog.jsx";
import EndOfDayLoadRemovalDialog from "./EndOfDayLoadRemovalDialog.jsx";
import {
  downloadDataManagerOrdersPdf,
  isExportApiError,
  markExportDocumentWindowFailed,
  openExportDocumentWindow,
} from "../lib/exportApi.js";
import { useToast } from "./ResultsPrimitives.jsx";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { updateOrderLoadingReference } from "../lib/dataManagerApi.js";
import { publishOrderDataChange } from "../utils/orderDataSync.js";
import DataManagerOrdersGrid from "./DataManagerOrdersGrid.jsx";
import DataManagerOrderExecutionDialog from "./DataManagerOrderExecutionDialog.jsx";
import useDataManagerEditConflicts from "../hooks/useDataManagerEditConflicts.js";
import useDataManagerOrdersFilters from "../hooks/useDataManagerOrdersFilters.js";
import useDataManagerColumnPreferences from "../hooks/useDataManagerColumnPreferences.js";
import useDataManagerFrozenPanes from "../hooks/useDataManagerFrozenPanes.js";
import useDataManagerTableSortState from "../hooks/useDataManagerTableSortState.js";
import { getEndOfDayLoadModeLongLabel } from "../utils/endOfDayLoadModeLabels.js";
import { getSegmentHoverSurfaceClassName } from "../utils/fleetSegmentPresentation.js";
import { resolveNextDispatchDateValue } from "../utils/dispatchDateUrls.js";
import { buildDeliveryDriverOptions } from "../utils/dataManagerDeliveryDrivers.js";
import { createDataManagerColumnResizePreview } from "../utils/dataManagerColumnResizePreview.js";
import {
  buildDataManagerOrdersExportViewModel,
  buildOrdersActiveFilters,
  buildOrdersScopeLabel,
} from "../utils/buildDataManagerOrdersExportViewModel.js";
import { buildOrderAdvancedFilterChips, EMPTY_ORDER_ADVANCED_FILTERS, isDeliveryDateRangeFilterActive, matchOrderAdvancedFilters } from "../utils/dataManagerOrderSearch.js";
import {
  ORDER_ADVANCED_FILTER_CATALOG,
  ORDER_DEFAULT_ADVANCED_FILTER_KEYS,
  ORDER_FILTERS_PREFERENCE_KEY,
  ORDER_PDF_COLUMN_CATALOG,
  ORDER_PDF_DEFAULT_COLUMN_KEYS,
  ORDER_PDF_PREFERENCE_KEY,
  buildOrderPdfExportColumns,
  buildPersistedOrderPdfColumnLabels,
  normalizeOrderPdfColumnLabels,
  resolveOrderPdfColumnKeys,
  resolveOrderPdfColumnLabels,
  resolveVisibleOrderFilterKeys,
} from "../utils/dataManagerOrdersCustomization.js";
import {
  ORDER_ADDRESS_FIELDS,
  ORDER_ADDRESS_FIELD_LABELS,
  getOrderGridTrailingColumns,
  ORDER_GRID_COLUMNS,
  ORDER_GRID_COLUMN_MIN_WIDTHS,
  buildAddressValidationToast,
  buildCreateDeliveryDraftPatch,
  buildCreateOrderBillToPatch,
  buildCreatePayloads,
  buildEndOfDayLoadOrderTruckIndex,
  buildEndOfDayLoadSourceTruckOptions,
  buildDeletePayload,
  buildDeliveryClientDraftPatch,
  buildUpdatePayload,
  coerceLoadingClientDraftValue,
  addressHasCivicNumber,
  createEditableOrderDraft,
  createNextCreateOrderDelivery,
  createNewOrderDraft,
  findCreateOrderDelivery,
  formatDateDisplay,
  formatDateInput,
  getCreateAddressValidationKey,
  getCreateOrderDeliveries,
  getOrderAddressValidationKey,
  getOrderDraftErrors,
  hasDraftErrors,
  hasOrderAddressFieldChanged,
  hasOrderDraftChanges,
  isSelectedClientCanonicalAddress,
  resolveOrderSaveDisabledReason,
  isOrderAddressField,
  normalizeOptionalString,
  resolveEndOfDayLoadGroupActionFromOrder,
  resolveSelectedEndOfDayLoadGroupAction,
  resolveAddressValidationFailureMessage,
  resolveLoadingClientDraftValueForSave,
  resolveAddressValidationToastPositionStyle,
  resolveMutationErrorMessage,
  resolveOrderBrowserDeliveryDate,
  resolveOrderGridColumnWidth,
  normalizeOrderGridColumnWidths,
  normalizeOrderTariffColumnPreferences,
  resolveEndOfDayLoadSourceTruckPolicyMessage,
  toDateInputValue,
  isMissingCivicNumberConfirmationCancelledError,
  isOrderBillingApproved,
  isOrderEndOfDayLoadLocked,
  isLoadingClientSuggestionsTarget,
  MissingCivicNumberConfirmationCancelledError,
  isPointerInsideConfirmationModal,
  buildCoopClientNames,
} from "../hooks/useDataManagerOrdersUtils.js";
import useMissingCivicNumberConfirmation from "../hooks/useMissingCivicNumberConfirmation.jsx";
import {
  buildOrderGridFreezableColumnDescriptors,
} from "../utils/dataManagerFrozenPanes.js";
import {
  canOpenGeneralOrderEdit,
} from "../utils/dataManagerOrderApprovals.js";
import {
  resolveInitialOrdersSortState,
  resolveNextDataManagerSortState,
} from "../utils/dataManagerTableSort.js";
import {
  buildEndOfDayLoadTargetTrucksEmptyMessage,
  filterEndOfDayLoadSourceTrucks,
  filterEndOfDayLoadTargetTrucks,
  resolveEndOfDayLoadSourceProductLabel,
  resolveEndOfDayLoadTrailerFieldLabel,
  resolveEndOfDayLoadTrailerLabel,
} from "../utils/fleetReassignmentHelpers.js";
import {
  clearDataManagerOrderCreateDraftState,
  persistDataManagerOrderCreateDraftState,
  readDataManagerOrderCreateDraftState,
} from "../utils/dataManagerOrderCreateDraftStorage.js";

const ORDER_DEFAULT_FOCUS_KEY = "client_code";
const FROZEN_DISPATCH_MUTATION_MESSAGE = "Dégelez la commande dans le Dispatch avant modification.";

function resolveFrozenDispatchMutationMessage(error) {
  const message = resolveMutationErrorMessage(error, "Impossible d'enregistrer la commande.");
  return message.includes(FROZEN_DISPATCH_MUTATION_MESSAGE) ? message : "";
}

function buildLocalDispatchSaveNoticeClassName(tone = "error") {
  if (tone === "success") {
    return "rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm font-semibold text-green-800";
  }

  return "rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800";
}

function buildCreatedOrdersSaveNoticeMessage(createdOrderIds = []) {
  const safeCreatedOrderIds = (Array.isArray(createdOrderIds) ? createdOrderIds : [])
    .map((value) => normalizeOptionalString(value))
    .filter(Boolean);

  if (safeCreatedOrderIds.length === 1) {
    return `Commande ${safeCreatedOrderIds[0]} sauvegardée.`;
  }

  if (safeCreatedOrderIds.length > 1) {
    return `${safeCreatedOrderIds.length} commandes sauvegardées.`;
  }

  return "Commande sauvegardée.";
}

function resolveTruckLabelFromRows(truckRows = [], truckId = "") {
  const safeTruckId = normalizeOptionalString(truckId);
  if (!safeTruckId) {
    return "";
  }
  const truck = (Array.isArray(truckRows) ? truckRows : [])
    .find((candidate) => normalizeOptionalString(candidate?.truck_id || candidate?.id) === safeTruckId);
  return normalizeOptionalString(truck?.truck_name || truck?.name || truck?.label) || safeTruckId;
}

function normalizeEndOfDayLoadDeliveryMode(value = "") {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  return normalizedValue === "evening" || normalizedValue === "soir" || normalizedValue === "livrer_soir"
    ? "evening"
    : "next_day";
}

function normalizeExplicitEndOfDayLoadDeliveryMode(value = "") {
  const normalizedValue = typeof value === "string" ? value.trim().toLowerCase() : "";
  if (normalizedValue === "evening" || normalizedValue === "soir" || normalizedValue === "livrer_soir") {
    return "evening";
  }
  if (
    normalizedValue === "next_day"
    || normalizedValue === "tomorrow"
    || normalizedValue === "demain"
    || normalizedValue === "day"
    || normalizedValue === "jour"
    || normalizedValue === "livrer_demain"
  ) {
    return "next_day";
  }
  return "";
}

function resolveEndOfDayLoadTargetPeriod(deliveryMode = "next_day") {
  return normalizeEndOfDayLoadDeliveryMode(deliveryMode) === "evening" ? "evening" : "day";
}

function resolveEndOfDayLoadDeliveryModeFromRecord(record = null, fallbackSourceDispatchDate = "") {
  const explicitMode = normalizeExplicitEndOfDayLoadDeliveryMode(record?.end_of_day_load_delivery_mode);
  if (explicitMode) {
    return explicitMode;
  }

  const targetPeriod = normalizeExplicitEndOfDayLoadDeliveryMode(record?.end_of_day_load_target_period);
  if (targetPeriod) {
    return targetPeriod;
  }

  const sourceDispatchDate = normalizeOptionalString(record?.end_of_day_load_source_dispatch_date) || fallbackSourceDispatchDate;
  const targetDispatchDate = normalizeOptionalString(record?.end_of_day_load_target_dispatch_date);
  if (sourceDispatchDate && targetDispatchDate && sourceDispatchDate === targetDispatchDate) {
    return "evening";
  }

  return "next_day";
}

function resolveEndOfDayLoadTargetDispatchDateForMode(sourceDispatchDate = "", deliveryMode = "next_day", currentTargetDispatchDate = "") {
  const safeSourceDispatchDate = normalizeOptionalString(sourceDispatchDate) || "";
  const safeMode = normalizeEndOfDayLoadDeliveryMode(deliveryMode);
  if (safeMode === "evening") {
    return safeSourceDispatchDate;
  }

  const safeCurrentTargetDispatchDate = normalizeOptionalString(currentTargetDispatchDate) || "";
  return safeCurrentTargetDispatchDate && safeCurrentTargetDispatchDate !== safeSourceDispatchDate
    ? safeCurrentTargetDispatchDate
    : resolveNextDispatchDateValue(safeSourceDispatchDate);
}

function normalizePayloadStringList(value) {
  if (!Array.isArray(value)) {
    return [];
  }
  return [...new Set(value.map((entry) => normalizeOptionalString(entry)).filter(Boolean))];
}

function resolveOrdersColumnPrefs(savedColumnPrefs = null) {
  if (savedColumnPrefs?.orders) {
    return savedColumnPrefs.orders;
  }
  if (savedColumnPrefs?.column_order || savedColumnPrefs?.column_widths) {
    return savedColumnPrefs;
  }
  return null;
}

function resolvePreferenceEntry(savedColumnPrefs = null, preferenceKey = "") {
  if (!savedColumnPrefs || !preferenceKey) {
    return null;
  }
  return savedColumnPrefs[preferenceKey] ?? null;
}

function normalizeVisibleFilterKeys(keys = []) {
  const allowedKeys = new Set(ORDER_ADVANCED_FILTER_CATALOG.map((filter) => filter.key));
  const seenKeys = new Set();
  return (Array.isArray(keys) ? keys : []).filter((key) => {
    if (!allowedKeys.has(key) || seenKeys.has(key)) {
      return false;
    }
    seenKeys.add(key);
    return true;
  });
}

function pruneAdvancedFiltersForVisibleKeys(filters = {}, visibleFilterKeys = []) {
  const visibleKeySet = new Set(visibleFilterKeys);
  const nextFilters = {
    ...EMPTY_ORDER_ADVANCED_FILTERS,
    scope: filters?.scope || "",
  };

  ORDER_ADVANCED_FILTER_CATALOG.forEach((filter) => {
    if (!visibleKeySet.has(filter.key)) {
      return;
    }
    if (filter.type === "number-range" || filter.type === "date-range") {
      nextFilters[`${filter.key}_min`] = filters?.[`${filter.key}_min`] || "";
      nextFilters[`${filter.key}_max`] = filters?.[`${filter.key}_max`] || "";
      return;
    }
    nextFilters[filter.key] = filters?.[filter.key] || "";
  });

  return nextFilters;
}

function clearAdvancedFilterValue(filters = {}, filterKey = "") {
  const filter = ORDER_ADVANCED_FILTER_CATALOG.find((entry) => entry.key === filterKey);
  if (!filter) {
    return filters;
  }
  if (filter.type === "number-range" || filter.type === "date-range") {
    return {
      ...filters,
      [`${filter.key}_min`]: "",
      [`${filter.key}_max`]: "",
    };
  }
  return {
    ...filters,
    [filter.key]: "",
  };
}

function hasAdvancedFilterValue(filters = {}, visibleFilterKeys = []) {
  const visibleKeySet = new Set(visibleFilterKeys);
  return ORDER_ADVANCED_FILTER_CATALOG.some((filter) => {
    if (!visibleKeySet.has(filter.key)) {
      return false;
    }
    if (filter.type === "number-range" || filter.type === "date-range") {
      return Boolean(filters?.[`${filter.key}_min`] || filters?.[`${filter.key}_max`]);
    }
    return Boolean(filters?.[filter.key]);
  });
}

function hasVisibleLoadingClientFilter(visibleFilterKeys = []) {
  const visibleKeySet = new Set(visibleFilterKeys);
  return ORDER_ADVANCED_FILTER_CATALOG.some((filter) => (
    filter.type === "loading-client" && visibleKeySet.has(filter.key)
  ));
}

export default function DataManagerOrdersTab({
  toolbar = null,
  productCatalog = [],
  productCodes = [],
  onOpenConsolidation = null,
  restoredConsolidationContext = null,
  canBulkDeleteOrders = false,
  canOpenConsolidation = false,
  canClearDeliveredOrders = false,
  canDeleteDeliveredOrders = false,
  canEditOrders = false,
  canExportOrders = false,
  canMarkOrdersDelivered = false,
  canWriteOrderDeliveryDriver = false,
  canWriteOrderActualTonnage = false,
  assignOrdersEndOfDayLoad = null,
  removeOrdersEndOfDayLoad = null,
  clearOrderDeliveredStatus,
  clientCorrespondences = [],
  createOrder,
  createOrders,
  currentDispatchDates = [],
  currentDispatchOrderCount = 0,
  contextualDispatchPeriod = "day",
  deleteOrder,
  ensureSectionsLoaded = null,
  isPending,
  isBrowseMode = false,
  isPendingBillingApprovalFilter = false,
  isOrderBrowserOpen = false,
  isRecentBrowseLimited = false,
  onApplyOrderBrowser = null,
  onCloseOrderBrowser = null,
  onOpenOrderBrowser = null,
  onQuickSelectOrderBrowseDate = null,
  onTogglePendingBillingApprovalFilter = null,
  onRequireFullOrdersCatalog = null,
  onResetOrderScope = null,
  onSearchQueryChange = null,
  orderBrowseDateFilter = "",
  orders,
  pendingBillingApprovalCount = 0,
  refreshState = null,
  saveColumnPreferences = null,
  savedColumnPrefs = null,
  searchQuery = "",
  totalOrderCount = 0,
  trucks = [],
  updateOrder,
  updateOrderExecution = null,
  validateOrderAddress,
}) {
  const { dismissToast, showToast } = useToast();
  const { missingCivicNumberDialog, requestMissingCivicNumberConfirmation } = useMissingCivicNumberConfirmation();
  const rootRef = React.useRef(null);
  const addressValidationToastIdRef = React.useRef(null);
  const addressValidationFieldRefsRef = React.useRef({});
  const initialCreateOrderDraftStateRef = React.useRef(null);
  const savedOrdersColumnPrefs = resolveOrdersColumnPrefs(savedColumnPrefs);
  const initialOrderFilterPrefs = resolvePreferenceEntry(savedColumnPrefs, ORDER_FILTERS_PREFERENCE_KEY);
  const initialOrderPdfPrefs = resolvePreferenceEntry(savedColumnPrefs, ORDER_PDF_PREFERENCE_KEY);
  const handleColumnPreferencesSaveError = React.useCallback((error) => {
    const errorDetail = error instanceof Error && error.message
      ? error.message
      : "Impossible de sauvegarder les préférences de colonnes.";
    showToast({
      duration: 7000,
      title: "Préférences de colonnes non enregistrées",
      message: `${errorDetail} La dernière configuration synchronisée a été restaurée.`,
      type: "error",
    });
  }, [showToast]);
  if (!initialCreateOrderDraftStateRef.current) {
    initialCreateOrderDraftStateRef.current = readDataManagerOrderCreateDraftState() || {
      draft: createNewOrderDraft(),
      isCreatingOrder: false,
    };
  }
  const [isCreatingOrder, setIsCreatingOrder] = React.useState(() => initialCreateOrderDraftStateRef.current.isCreatingOrder);
  const [isCreateOrderDialogOpen, setIsCreateOrderDialogOpen] = React.useState(false);
  const [newOrderDraft, setNewOrderDraft] = React.useState(() => initialCreateOrderDraftStateRef.current.draft);
  const [editingOrderId, setEditingOrderId] = React.useState(null);
  const editingOrderSourceRef = React.useRef(null);
  const [editingOrderFocusKey, setEditingOrderFocusKey] = React.useState(ORDER_DEFAULT_FOCUS_KEY);
  const [sortState, setSortState] = useDataManagerTableSortState(
    "orders",
    () => resolveInitialOrdersSortState(contextualDispatchPeriod),
  );
  React.useLayoutEffect(() => {
    if (!restoredConsolidationContext) return;
    if (restoredConsolidationContext.sortState) setSortState(restoredConsolidationContext.sortState);
    const frame = requestAnimationFrame(() => restoreOrdersScroll(rootRef.current, restoredConsolidationContext.scroll));
    return () => cancelAnimationFrame(frame);
  }, []);
  const [orderDrafts, setOrderDrafts] = React.useState({});
  const [selectedOrderIds, setSelectedOrderIds] = React.useState(() => restoredConsolidationContext?.selectedOrderIds || {});
  const [focusedOrderId, setFocusedOrderId] = React.useState(null);
  const [isDeleteSelectionDialogOpen, setIsDeleteSelectionDialogOpen] = React.useState(false);
  const [isDeletingSelection, setIsDeletingSelection] = React.useState(false);
  const [endOfDayLoadOrderTarget, setEndOfDayLoadOrderTarget] = React.useState(null);
  const [endOfDayLoadGroupTarget, setEndOfDayLoadGroupTarget] = React.useState(null);
  const [endOfDayLoadLoadedTrucks, setEndOfDayLoadLoadedTrucks] = React.useState([]);
  const [endOfDayLoadSourceDispatchDate, setEndOfDayLoadSourceDispatchDate] = React.useState("");
  const [endOfDayLoadSourceTruckId, setEndOfDayLoadSourceTruckId] = React.useState("");
  const [endOfDayLoadSourceTrucksState, setEndOfDayLoadSourceTrucksState] = React.useState({
    dispatchDate: "",
    errorMessage: "",
    isStaleSnapshot: false,
    isLoading: false,
    options: [],
    sourceChangeTruckPolicy: null,
  });
  const [endOfDayLoadTargetDispatchDate, setEndOfDayLoadTargetDispatchDate] = React.useState("");
  const endOfDayLoadDayTargetDispatchDateRef = React.useRef("");
  const [endOfDayLoadDeliveryMode, setEndOfDayLoadDeliveryMode] = React.useState("next_day");
  const [isEndOfDayLoadRemoveConfirmOpen, setIsEndOfDayLoadRemoveConfirmOpen] = React.useState(false);
  const [isEndOfDayLoadLocalProcessing, setIsEndOfDayLoadLocalProcessing] = React.useState(false);
  const [localDispatchSaveNotice, setLocalDispatchSaveNotice] = React.useState(null);
  const [clearDeliveredOrderTarget, setClearDeliveredOrderTarget] = React.useState(null);
  const [orderExecutionTarget, setOrderExecutionTarget] = React.useState(null);
  const [orderExecutionErrorMessage, setOrderExecutionErrorMessage] = React.useState("");
  const [addressValidationPendingKeys, setAddressValidationPendingKeys] = React.useState({});
  const [addressValidationErrors, setAddressValidationErrors] = React.useState({});
  const validatedAddressOptionsRef = React.useRef({});
  const [browserDeliveryDate, setBrowserDeliveryDate] = React.useState(() => resolveOrderBrowserDeliveryDate({
    currentDispatchDates,
    isBrowseMode,
    orderBrowseDateFilter,
  }));
  const [isPmOnlyFilter, setIsPmOnlyFilter] = React.useState(restoredConsolidationContext?.isPmOnlyFilter || false);
  const [advancedFilters, setAdvancedFilters] = React.useState(restoredConsolidationContext?.advancedFilters || { ...EMPTY_ORDER_ADVANCED_FILTERS });
  const [draftAdvancedFilters, setDraftAdvancedFilters] = React.useState({ ...EMPTY_ORDER_ADVANCED_FILTERS, scope: isBrowseMode ? "browse" : "dispatch" });
  const [isAdvancedFiltersOpen, setIsAdvancedFiltersOpen] = React.useState(false);
  const [isCustomizingFilters, setIsCustomizingFilters] = React.useState(false);
  const [visibleFilterKeys, setVisibleFilterKeys] = React.useState(() => resolveVisibleOrderFilterKeys(initialOrderFilterPrefs));
  const [draftVisibleFilterKeys, setDraftVisibleFilterKeys] = React.useState(() => resolveVisibleOrderFilterKeys(initialOrderFilterPrefs));
  const [isPdfDialogOpen, setIsPdfDialogOpen] = React.useState(false);
  const [isPdfExporting, setIsPdfExporting] = React.useState(false);
  const [committedPdfColumnKeys, setCommittedPdfColumnKeys] = React.useState(() => resolveOrderPdfColumnKeys(initialOrderPdfPrefs));
  const [committedPdfColumnLabels, setCommittedPdfColumnLabels] = React.useState(() => resolveOrderPdfColumnLabels(initialOrderPdfPrefs));
  const [pdfSelectedColumnKeys, setPdfSelectedColumnKeys] = React.useState(() => resolveOrderPdfColumnKeys(initialOrderPdfPrefs));
  const [pdfColumnLabels, setPdfColumnLabels] = React.useState(() => resolveOrderPdfColumnLabels(initialOrderPdfPrefs));
  const confirmedVisibleFilterKeysRef = React.useRef([...visibleFilterKeys]);
  const confirmedPdfPreferencesRef = React.useRef({
    columnKeys: [...committedPdfColumnKeys],
    columnLabels: { ...committedPdfColumnLabels },
  });
  const filterPreferenceSaveChainRef = React.useRef(Promise.resolve());
  const filterPreferenceSaveRevisionRef = React.useRef(0);
  const pdfPreferenceSaveChainRef = React.useRef(Promise.resolve());
  const pdfPreferenceSaveRevisionRef = React.useRef(0);
  const searchInputRef = React.useRef(null);
  const orderColumnResizeCleanupRef = React.useRef(null);
  const [orderColumnWidths, setOrderColumnWidths] = React.useState(() => {
    const savedWidths = savedOrdersColumnPrefs?.column_widths;
    return normalizeOrderGridColumnWidths(savedWidths);
  });
  const orderColumnWidthsRef = React.useRef(orderColumnWidths);
  const pendingCreatedOrderIdRef = React.useRef(null);
  const pendingCreatedOrderScrollIdRef = React.useRef(null);

  React.useEffect(() => {
    persistDataManagerOrderCreateDraftState({
      draft: newOrderDraft,
      isCreatingOrder,
    });
  }, [isCreatingOrder, newOrderDraft]);

  React.useEffect(() => {
    orderColumnWidthsRef.current = orderColumnWidths;
  }, [orderColumnWidths]);

  const tariffColumnPreferences = React.useMemo(
    () => normalizeOrderTariffColumnPreferences(savedOrdersColumnPrefs ?? null),
    [savedOrdersColumnPrefs],
  );
  const orderColumnPrefs = useDataManagerColumnPreferences({
    baseColumns: ORDER_GRID_COLUMNS,
    getPersistedColumnWidths: () => orderColumnWidthsRef.current,
    onSaveError: handleColumnPreferencesSaveError,
    saveColumnPreferences,
    savedPrefs: tariffColumnPreferences,
    normalizeIncoming: normalizeOrderTariffColumnPreferences,
    tableKey: "orders",
  });
  const orderColumnPrefsOrderRef = React.useRef(orderColumnPrefs.resolvedColumnOrder);
  React.useEffect(() => {
    orderColumnPrefsOrderRef.current = orderColumnPrefs.resolvedColumnOrder;
  }, [orderColumnPrefs.resolvedColumnOrder]);
  const savedOrderFilterPrefs = useLiveUserUiPreference(`columns:${ORDER_FILTERS_PREFERENCE_KEY}`, initialOrderFilterPrefs, isCustomizingFilters);
  const savedOrderPdfPrefs = useLiveUserUiPreference(`columns:${ORDER_PDF_PREFERENCE_KEY}`, initialOrderPdfPrefs, isPdfDialogOpen || isPdfExporting);
  const savedFilterPreferenceSignature = JSON.stringify(savedOrderFilterPrefs?.column_order ?? null);
  const savedPdfPreferenceSignature = JSON.stringify({
    columnLabels: savedOrderPdfPrefs?.column_labels ?? null,
    columnOrder: savedOrderPdfPrefs?.column_order ?? null,
  });

  React.useEffect(() => {
    const nextVisibleFilterKeys = resolveVisibleOrderFilterKeys(savedOrderFilterPrefs);
    confirmedVisibleFilterKeysRef.current = [...nextVisibleFilterKeys];
    setVisibleFilterKeys(nextVisibleFilterKeys);
    if (!isCustomizingFilters) {
      setDraftVisibleFilterKeys(nextVisibleFilterKeys);
    }
  }, [savedFilterPreferenceSignature]);

  React.useEffect(() => {
    const nextColumnKeys = resolveOrderPdfColumnKeys(savedOrderPdfPrefs);
    const nextColumnLabels = resolveOrderPdfColumnLabels(savedOrderPdfPrefs);
    confirmedPdfPreferencesRef.current = {
      columnKeys: [...nextColumnKeys],
      columnLabels: { ...nextColumnLabels },
    };
    setCommittedPdfColumnKeys(nextColumnKeys);
    setCommittedPdfColumnLabels(nextColumnLabels);
    if (!isPdfDialogOpen) {
      setPdfSelectedColumnKeys(nextColumnKeys);
      setPdfColumnLabels(nextColumnLabels);
    }
  }, [savedPdfPreferenceSignature]);

  const ordersForGrid = React.useMemo(
    () => {
      let nextOrders = isPmOnlyFilter
        ? orders.filter((order) => isOrderEndOfDayLoadLocked(order))
        : orders;
      nextOrders = nextOrders.filter((order) => matchOrderAdvancedFilters(order, advancedFilters));
      return nextOrders;
    },
    [advancedFilters, isPmOnlyFilter, orders],
  );
  const isEndOfDayLoadMutationPending = typeof isPending === "function" ? isPending("order:end-of-day-load") : false;
  const isEndOfDayLoadPending = isEndOfDayLoadMutationPending || isEndOfDayLoadLocalProcessing;
  const isEndOfDayLoadRemovePending = typeof isPending === "function" ? isPending("order:end-of-day-load-remove") : false;
  const clearDeliveredPending = clearDeliveredOrderTarget?.order_id && typeof isPending === "function"
    ? isPending(`order:clear-delivered:${clearDeliveredOrderTarget.order_id}`)
    : false;
  const orderExecutionPending = orderExecutionTarget?.order_id && typeof isPending === "function"
    ? isPending(`order:execution:${orderExecutionTarget.order_id}`)
    : false;
  const orderExecutionConflict = useDataManagerEditConflicts({
    original: editingOrderId ? editingOrderSourceRef.current : null,
    execution: orderExecutionTarget, orders, showToast,
    pending: Boolean(orderExecutionPending || (editingOrderId && isPending?.(`order:update:${editingOrderId}`))),
  });
  const endOfDayLoadTruckRows = Array.isArray(trucks) && trucks.length > 0 ? trucks : endOfDayLoadLoadedTrucks;
  const deliveryDriverOptions = React.useMemo(
    () => buildDeliveryDriverOptions(endOfDayLoadTruckRows),
    [endOfDayLoadTruckRows],
  );
  const endOfDayLoadSourceProductLabel = React.useMemo(() => {
    if (endOfDayLoadOrderTarget) {
      return resolveEndOfDayLoadSourceProductLabel({ order: endOfDayLoadOrderTarget });
    }
    if (endOfDayLoadGroupTarget?.orderIds?.length) {
      const selectedOrderIdSet = new Set(endOfDayLoadGroupTarget.orderIds);
      return resolveEndOfDayLoadSourceProductLabel({
        orders: orders.filter((order) => selectedOrderIdSet.has(order?.order_id)),
      });
    }
    return "";
  }, [endOfDayLoadGroupTarget, endOfDayLoadOrderTarget, orders]);
  // Le camion source livre aussi le Chargé PM (sa remorque) : même filtre qu'une cible du Gantt, aspirateur,
  // Ontario et pas de Dompeur pour un groupe ; la protection compte à la date cible, que le serveur vérifie.
  const endOfDayLoadTargetFilters = React.useMemo(() => {
    const selectedOrders = endOfDayLoadOrderTarget
      ? [endOfDayLoadOrderTarget]
      : orders.filter((order) => (endOfDayLoadGroupTarget?.orderIds || []).includes(order?.order_id));
    const values = (key) => selectedOrders.flatMap((order) => [order?.[key], ...(order?.deliveries || []).map((delivery) => delivery?.[key])])
      .map((value) => (value === true ? "oui" : String(value ?? "").trim().toLowerCase()));
    return {
      excludeDompeurTargets: selectedOrders.length > 1,
      includeProtected: true,
      requiresAspirator: values("aspirator").some((value) => value === "oui" || value === "long"),
      requiresOntario: values("ontario").some((value) => value === "oui" || value === "true"),
    };
  }, [endOfDayLoadGroupTarget, endOfDayLoadOrderTarget, orders]);
  const {
    allVisibleDeletableOrdersSelected,
    allVisibleSelectableOrdersSelected,
    canApplyBrowserFilters,
    canCreate,
    cancelledOrderCount,
    createAddressValidationPending,
    createBusy,
    createDeliveryCount,
    createDisabledReason,
    createDisabledMessages,
    createLoadingAddressPreview,
    createOrderConfirmationSummaryItems,
    createOrderDeliveries,
    createPending,
    deletableVisibleOrders,
    dispatchDateSummary,
    effectiveNewOrderErrors,
    isGlobalSearchMode,
    loadingClientOptions,
    loadingClientOptionsByLookupKey,
    lockedOrderCount,
    orderTableColumnStyles,
    orderTableStyle,
    primaryCreateOrderDelivery,
    selectedDeleteCount,
    selectedOrderCount,
    selectedVisibleOrders,
    selectedVisibleDeleteOrders,
    selectableVisibleOrders,
    showBulkDeleteControls,
    showOrderSelectionControls,
    sortedOrders,
    activeSearchQuery,
    trimmedSearchQuery,
    visibleNewOrderErrors,
    visibleOrderCount,
  } = useDataManagerOrdersFilters({
    productCatalog,
    addressValidationErrors,
    addressValidationPendingKeys,
    browserDeliveryDate,
    // Une commande : « Modifier les commandes » ; plusieurs à la fois : « Supprimer plusieurs commandes ».
    canBulkDeleteOrders: canBulkDeleteOrders || canEditOrders,
    canAssignEndOfDayLoad: typeof assignOrdersEndOfDayLoad === "function",
    canOpenConsolidation,
    canDeleteDeliveredOrders,
    clientCorrespondences,
    currentDispatchDates,
    isBrowseMode,
    isPending,
    newOrderDraft,
    orderColumnWidths,
    orders: ordersForGrid,
    searchQuery,
    selectedOrderIds,
    sortState,
  });
  const freezableOrderColumnDescriptors = React.useMemo(
    () => buildOrderGridFreezableColumnDescriptors({
      orderColumnWidths,
      resolvedOrderColumns: orderColumnPrefs.resolvedColumns,
      showSelectionColumn: showOrderSelectionControls,
      trailingColumns: getOrderGridTrailingColumns(),
    }),
    [orderColumnPrefs.resolvedColumns, orderColumnWidths, showOrderSelectionControls],
  );
  const {
    frozenColumnKeys,
    frozenColumnLabels,
    hasFrozenPanes,
    isFrozenColumn,
    toggleColumnFreeze,
    unfreezePanes,
  } = useDataManagerFrozenPanes("orders", freezableOrderColumnDescriptors, {
    onFrozenColumnKeysChange: orderColumnPrefs.handleFrozenColumnKeysChange,
    savedFrozenColumnKeys: orderColumnPrefs.savedFrozenColumnKeys,
  });
  const selectedEndOfDayLoadGroupAction = React.useMemo(
    () => resolveSelectedEndOfDayLoadGroupAction(sortedOrders, selectedOrderIds),
    [selectedOrderIds, sortedOrders],
  );
  const bulkBillingApprovalSelectionMeta = React.useMemo(
    () => ({ canOpenPreflight: selectedVisibleOrders.length > 0, buttonLabel: "Ouvrir dans Consolidation" }),
    [selectedVisibleOrders],
  );

  const ensureClientCorrespondencesReady = React.useCallback(async () => {
    if (typeof ensureSectionsLoaded !== "function") {
      return true;
    }

    try {
      await ensureSectionsLoaded(["client_correspondences"], {
        actionKey: "section:client_correspondences",
      });
      return true;
    } catch (error) {
      showToast({
        duration: 8000,
        title: "Chargement impossible",
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de charger les clients.",
        type: "error",
      });
      return false;
    }
  }, [ensureSectionsLoaded, showToast]);

  React.useEffect(() => {
    if (!isCreatingOrder) {
      return;
    }

    void ensureClientCorrespondencesReady();
  }, [ensureClientCorrespondencesReady, isCreatingOrder]);

  const shouldLoadClientsForAdvancedFilters = React.useMemo(
    () => hasVisibleLoadingClientFilter(visibleFilterKeys),
    [visibleFilterKeys],
  );

  React.useEffect(() => {
    if (!isAdvancedFiltersOpen || !shouldLoadClientsForAdvancedFilters) {
      return;
    }

    void ensureClientCorrespondencesReady();
  }, [ensureClientCorrespondencesReady, isAdvancedFiltersOpen, shouldLoadClientsForAdvancedFilters]);

  const ensureEndOfDayLoadTrucksReady = React.useCallback(async () => {
    const currentTrucks = Array.isArray(trucks) ? trucks : [];
    if (currentTrucks.length > 0) {
      return currentTrucks;
    }
    if (endOfDayLoadLoadedTrucks.length > 0) {
      return endOfDayLoadLoadedTrucks;
    }
    if (typeof ensureSectionsLoaded !== "function") {
      return currentTrucks;
    }

    try {
      const nextState = await ensureSectionsLoaded(["trucks"], {
        actionKey: "section:trucks",
      });
      const nextTrucks = Array.isArray(nextState?.trucks) ? nextState.trucks : [];
      setEndOfDayLoadLoadedTrucks(nextTrucks);
      return nextTrucks;
    } catch (error) {
      showToast({
        duration: 8000,
        title: "Chargement impossible",
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de charger les camions.",
        type: "error",
      });
      return null;
    }
  }, [endOfDayLoadLoadedTrucks, ensureSectionsLoaded, showToast, trucks]);

  const dismissAddressValidationToast = React.useCallback(() => {
    const toastId = addressValidationToastIdRef.current;
    if (!toastId) {
      return;
    }

    dismissToast(toastId);
    addressValidationToastIdRef.current = null;
  }, [dismissToast]);

  const registerAddressValidationFieldRef = React.useCallback((validationKey, node) => {
    if (!validationKey) {
      return;
    }

    if (node) {
      addressValidationFieldRefsRef.current[validationKey] = node;
      return;
    }

    delete addressValidationFieldRefsRef.current[validationKey];
  }, []);

  const showAddressValidationToast = React.useCallback((toastPayload, validationKey) => {
    dismissAddressValidationToast();

    const anchorElement = validationKey ? addressValidationFieldRefsRef.current[validationKey] : null;
    const positionStyle = resolveAddressValidationToastPositionStyle(anchorElement);
    addressValidationToastIdRef.current = showToast({
      ...toastPayload,
      ...(positionStyle ? { positionStyle } : {}),
    });
  }, [dismissAddressValidationToast, showToast]);

  const setAddressValidationPending = React.useCallback((validationKey, isPendingValue) => {
    setAddressValidationPendingKeys((currentState) => {
      if (isPendingValue) {
        return {
          ...currentState,
          [validationKey]: true,
        };
      }

      if (!currentState[validationKey]) {
        return currentState;
      }

      const nextState = { ...currentState };
      delete nextState[validationKey];
      return nextState;
    });
  }, []);

  const setAddressValidationError = React.useCallback((validationKey, message) => {
    setAddressValidationErrors((currentState) => {
      const resolvedMessage = typeof message === "string" ? message.trim() : "";
      if (!resolvedMessage) {
        if (!currentState[validationKey]) {
          return currentState;
        }

        const nextState = { ...currentState };
        delete nextState[validationKey];
        return nextState;
      }

      if (currentState[validationKey] === resolvedMessage) {
        return currentState;
      }

      return {
        ...currentState,
        [validationKey]: resolvedMessage,
      };
    });
  }, []);

  const clearAddressValidationState = React.useCallback((validationKey) => {
    setAddressValidationPending(validationKey, false);
    setAddressValidationError(validationKey, "");
  }, [setAddressValidationError, setAddressValidationPending]);

  React.useEffect(() => () => {
    dismissAddressValidationToast();
  }, [dismissAddressValidationToast]);

  const handleOrderColumnResizePointerDown = React.useCallback((event, columnKey) => {
    if (!columnKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const startX = Number(event.clientX) || 0;
    const startWidth = resolveOrderGridColumnWidth(columnKey, orderColumnWidths);
    const minWidth = Number(ORDER_GRID_COLUMN_MIN_WIDTHS[columnKey]) || 0;
    const resizeHeaderCell = event.currentTarget?.closest?.("th") ?? null;
    const resizeTable = event.currentTarget?.closest?.("table") ?? null;
    let animationFrameId = null;
    let pendingWidth = startWidth;
    orderColumnResizeCleanupRef.current?.();
    const resizePreview = createDataManagerColumnResizePreview({
      headerCell: resizeHeaderCell,
      pointerX: startX,
      table: resizeTable,
    });

    const applyPendingWidth = () => {
      animationFrameId = null;
      resizePreview.update(pendingWidth - startWidth);
    };

    const commitPendingWidth = () => {
      const currentWidths = orderColumnWidthsRef.current;
      if (currentWidths[columnKey] === pendingWidth) {
        return;
      }

      const nextWidths = {
        ...currentWidths,
        [columnKey]: pendingWidth,
      };
      orderColumnWidthsRef.current = nextWidths;
      setOrderColumnWidths(nextWidths);
    };

    const handlePointerMove = (moveEvent) => {
      const deltaX = (Number(moveEvent.clientX) || 0) - startX;
      pendingWidth = Math.max(minWidth, Math.round(startWidth + deltaX));
      if (animationFrameId === null) {
        animationFrameId = window.requestAnimationFrame(applyPendingWidth);
      }
    };

    const suppressClick = (clickEvent) => {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
      document.removeEventListener("click", suppressClick, true);
    };

    const cleanup = (cleanupOptions = null) => {
      const isUnmounting = cleanupOptions?.unmounting === true;
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      if (!isUnmounting) {
        commitPendingWidth();
      }
      resizePreview.remove();
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", cleanup, true);
      document.removeEventListener("pointercancel", cleanup, true);
      if (!isUnmounting) {
        document.addEventListener("click", suppressClick, true);
      }
      if (orderColumnResizeCleanupRef.current === cleanup) {
        orderColumnResizeCleanupRef.current = null;
      }
      const latestWidths = orderColumnWidthsRef.current;
      const order = orderColumnPrefsOrderRef.current;
      if (!isUnmounting && typeof saveColumnPreferences === "function" && order && latestWidths) {
        saveColumnPreferences("orders", {
          columnOrder: order,
          columnWidths: latestWidths,
        }).catch(() => {});
      }
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", cleanup, true);
    document.addEventListener("pointercancel", cleanup, true);
    orderColumnResizeCleanupRef.current = cleanup;
  }, [orderColumnWidths, saveColumnPreferences]);

  React.useEffect(() => () => {
    orderColumnResizeCleanupRef.current?.({ unmounting: true });
  }, []);

  const clearCreateAddressValidationStates = React.useCallback((draft = newOrderDraft) => {
    getCreateOrderDeliveries(draft).forEach((delivery) => {
      clearAddressValidationState(getCreateAddressValidationKey(delivery.draft_id, "address"));
    });
  }, [clearAddressValidationState, newOrderDraft]);

  const clearOrderAddressValidationStates = React.useCallback((orderId) => {
    ORDER_ADDRESS_FIELDS.forEach((fieldKey) => {
      clearAddressValidationState(getOrderAddressValidationKey(orderId, fieldKey));
    });
  }, [clearAddressValidationState]);

  const validateDraftAddressValue = React.useCallback(async ({
    fieldKey,
    validationKey,
    value,
    onResolvedValue,
    allowMissingCivicNumber = false,
    suppressSuccessToast = false,
  }) => {
    const normalizedValue = normalizeOptionalString(value);
    const fieldLabel = ORDER_ADDRESS_FIELD_LABELS[fieldKey] || "Adresse";
    let hasShownValidationToast = false;

    setAddressValidationError(validationKey, "");
    setAddressValidationPending(validationKey, true);

    try {
      if (typeof validateOrderAddress !== "function") {
        return normalizedValue || "";
      }

      const validationResult = await validateOrderAddress({
        address: normalizedValue,
        allow_missing_civic_number: allowMissingCivicNumber,
      });
      const failureMessage = resolveAddressValidationFailureMessage(fieldLabel, validationResult);
      if (failureMessage) {
        showAddressValidationToast({
          ...buildAddressValidationToast({
            fieldLabel,
            inputAddress: normalizedValue,
            resolvedAddress: validationResult?.resolved_address,
            validationResult,
          }),
          duration: 12000,
          emphasis: "high",
        }, validationKey);
        hasShownValidationToast = true;
        setAddressValidationError(validationKey, failureMessage);
        throw new Error(failureMessage);
      }

      const resolvedValue = normalizeOptionalString(validationResult?.resolved_address) || normalizedValue || "";
      validatedAddressOptionsRef.current[validationKey] = { address: resolvedValue, allow_missing_civic_number: allowMissingCivicNumber };
      if (!suppressSuccessToast) {
        showAddressValidationToast({
          ...buildAddressValidationToast({
            fieldLabel,
            inputAddress: normalizedValue,
            resolvedAddress: resolvedValue,
            validationResult,
          }),
          duration: 9000,
          emphasis: "high",
        }, validationKey);
        hasShownValidationToast = true;
      }
      onResolvedValue?.(resolvedValue);
      return resolvedValue;
    } catch (error) {
      if (isMissingCivicNumberConfirmationCancelledError(error)) {
        throw error;
      }

      const failureMessage = error instanceof Error && error.message
        ? error.message
        : `${fieldLabel} invalide ou introuvable.`;
      if (!hasShownValidationToast) {
        showAddressValidationToast({
          type: "error",
          title: `${fieldLabel} à vérifier`,
          message: `Saisie: ${normalizedValue || "Adresse non fournie."}. ${failureMessage}`,
          duration: 12000,
          emphasis: "high",
        }, validationKey);
      }
      setAddressValidationError(validationKey, failureMessage);
      throw error;
    } finally {
      setAddressValidationPending(validationKey, false);
    }
  }, [setAddressValidationError, setAddressValidationPending, showAddressValidationToast, validateOrderAddress]);

  const validateDraftAddressWithCivicCheck = React.useCallback(async (params) => {
    const normalizedValue = normalizeOptionalString(params.value);
    const confirmed = await requestMissingCivicNumberConfirmation(normalizedValue);
    if (!confirmed) {
      throw new MissingCivicNumberConfirmationCancelledError();
    }

    return validateDraftAddressValue({
      ...params,
      allowMissingCivicNumber: !addressHasCivicNumber(normalizedValue) && confirmed,
    });
  }, [requestMissingCivicNumberConfirmation, validateDraftAddressValue]);

  const handleCreateAddressEnterValidation = React.useCallback((deliveryDraftId, fieldKey = "address") => {
    if (!isOrderAddressField(fieldKey)) {
      return;
    }

    const delivery = findCreateOrderDelivery(newOrderDraft, deliveryDraftId);
    if (!delivery) {
      return;
    }

    void validateDraftAddressWithCivicCheck({
      fieldKey,
      validationKey: getCreateAddressValidationKey(deliveryDraftId, fieldKey),
      value: delivery[fieldKey],
      onResolvedValue: (resolvedValue) => updateNewOrderDeliveryValue(
        deliveryDraftId,
        fieldKey,
        resolvedValue,
        { preserveValidationToast: true }
      ),
    }).catch((error) => {
      if (!isMissingCivicNumberConfirmationCancelledError(error)) {
        return;
      }
    });
  }, [newOrderDraft, validateDraftAddressWithCivicCheck]);

  const handleOrderAddressEnterValidation = React.useCallback((order) => {
    if (!order?.order_id) {
      return;
    }

    const draft = orderDrafts[order.order_id] || createEditableOrderDraft(order);
    void validateDraftAddressWithCivicCheck({
      fieldKey: "address",
      validationKey: getOrderAddressValidationKey(order.order_id, "address"),
      value: draft.address,
      onResolvedValue: (resolvedValue) => updateOrderDraftValue(order.order_id, "address", resolvedValue, { preserveValidationToast: true }),
    })
      .then(() => handleSaveOrder(order, { addressAlreadyValidated: true }))
      .catch((error) => {
        if (!isMissingCivicNumberConfirmationCancelledError(error)) {
          return;
        }
      });
  }, [handleSaveOrder, orderDrafts, validateDraftAddressWithCivicCheck]);

  function getVisibleCreateDeliveryFieldError(deliveryIndex, fieldKey) {
    return visibleNewOrderErrors[`deliveries.${deliveryIndex}.${fieldKey}`] || "";
  }

  function handleSort(nextSortKey) {
    setSortState((currentSortState) => resolveNextDataManagerSortState(currentSortState, nextSortKey));
  }

  React.useEffect(() => {
    if (isOrderBrowserOpen || isBrowseMode) {
      return;
    }

    const nextBrowserDeliveryDate = resolveOrderBrowserDeliveryDate({
      currentDispatchDates,
      isBrowseMode: false,
      orderBrowseDateFilter: "",
    });
    setBrowserDeliveryDate((currentValue) => (currentValue === nextBrowserDeliveryDate ? currentValue : nextBrowserDeliveryDate));
  }, [currentDispatchDates, isBrowseMode, isOrderBrowserOpen]);

  React.useEffect(() => {
    if (!isOrderBrowserOpen) {
      return;
    }

    setBrowserDeliveryDate(resolveOrderBrowserDeliveryDate({
      currentDispatchDates,
      isBrowseMode,
      orderBrowseDateFilter,
    }));
  }, [currentDispatchDates, isBrowseMode, isOrderBrowserOpen, orderBrowseDateFilter]);

  React.useEffect(() => {
    setDraftAdvancedFilters((currentValue) => ({
      ...currentValue,
      scope: isBrowseMode ? "browse" : "dispatch",
    }));
  }, [isBrowseMode]);

  React.useEffect(() => {
    if (!Array.isArray(orders)) {
      return;
    }

    setOrderDrafts((currentDrafts) => Object.fromEntries(
      Object.entries(currentDrafts).filter(([orderId]) => orders.some((order) => order?.order_id === orderId))
    ));
  }, [orders]);

  React.useEffect(() => {
    const visibleSelectableOrderIds = new Set(selectableVisibleOrders.map((order) => order.order_id));
    setSelectedOrderIds((currentSelection) => {
      let hasChanged = false;
      const nextSelection = {};

      Object.entries(currentSelection).forEach(([orderId, isSelected]) => {
        if (!isSelected) {
          return;
        }
        if (visibleSelectableOrderIds.has(orderId)) {
          nextSelection[orderId] = true;
          return;
        }
        hasChanged = true;
      });

      return hasChanged ? nextSelection : currentSelection;
    });
  }, [selectableVisibleOrders]);

  React.useEffect(() => {
    if (selectedDeleteCount === 0) {
      setIsDeleteSelectionDialogOpen(false);
    }
  }, [selectedDeleteCount]);

  React.useEffect(() => {
    const sourceDispatchDate = normalizeOptionalString(endOfDayLoadSourceDispatchDate) || "";
    // Le camion qui porte la commande à sa date d'origine (chargée en premier) reste connu
    // pendant tout le dialogue, même si la date source change.
    if ((!endOfDayLoadOrderTarget && !endOfDayLoadGroupTarget) || !sourceDispatchDate) {
      setEndOfDayLoadSourceTrucksState((current) => ({
        dispatchDate: sourceDispatchDate,
        errorMessage: "",
        isStaleSnapshot: false,
        isLoading: false,
        options: [],
        orderTruckIds: endOfDayLoadOrderTarget || endOfDayLoadGroupTarget ? current.orderTruckIds : {},
        sourceChangeTruckPolicy: null,
      }));
      return undefined;
    }

    const abortController = new AbortController();
    setEndOfDayLoadSourceTrucksState((current) => ({
      dispatchDate: sourceDispatchDate,
      errorMessage: "",
      isStaleSnapshot: false,
      isLoading: true,
      options: [],
      orderTruckIds: current.orderTruckIds,
      sourceChangeTruckPolicy: null,
    }));
    const searchParams = new URLSearchParams();
    searchParams.set("dispatch_date", sourceDispatchDate);
    searchParams.set("dispatch_period", "day");
    searchParams.set("include_source_change_truck_policy", "true");

    fetchWithSupabaseAuth(`/api/results/latest?${searchParams.toString()}`, {
      signal: abortController.signal,
    })
      .then(async (response) => {
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // The status-specific message below remains clearer than a JSON parsing error.
        }
        const isStaleSnapshot = payload?.code === "latest_optimization_stale"
          || payload?.source_data_state?.stale === true;
        const rawSourceChangeTruckPolicy = payload?.source_change_truck_policy;
        const sourceChangeTruckPolicy = (
          rawSourceChangeTruckPolicy
          && typeof rawSourceChangeTruckPolicy === "object"
          && !Array.isArray(rawSourceChangeTruckPolicy)
        ) ? rawSourceChangeTruckPolicy : null;
        const canUseStaleSnapshotPolicy = isStaleSnapshot && sourceChangeTruckPolicy !== null;
        if (
          (!response.ok && !canUseStaleSnapshotPolicy)
          || (payload?.error && !isStaleSnapshot)
        ) {
          const apiMessage = normalizeOptionalString(
            typeof payload?.detail === "string" ? payload.detail : payload?.error,
          );
          throw new Error(
            response.status === 404
              ? "Aucun dispatch Jour optimisé n'est disponible pour cette date."
              : (apiMessage || "Impossible de charger les remorques du dispatch source."),
          );
        }
        return {
          isStaleSnapshot,
          payload,
          sourceChangeTruckPolicy,
        };
      })
      .then(({ isStaleSnapshot, payload, sourceChangeTruckPolicy }) => {
        if (abortController.signal.aborted) {
          return;
        }
        setEndOfDayLoadSourceTrucksState((current) => ({
          dispatchDate: sourceDispatchDate,
          errorMessage: "",
          isStaleSnapshot,
          isLoading: false,
          options: buildEndOfDayLoadSourceTruckOptions(payload?.assignments),
          orderTruckIds: { ...buildEndOfDayLoadOrderTruckIndex(payload?.assignments), ...current.orderTruckIds },
          sourceChangeTruckPolicy,
        }));
      })
      .catch((error) => {
        if (abortController.signal.aborted) {
          return;
        }
        setEndOfDayLoadSourceTrucksState((current) => ({
          dispatchDate: sourceDispatchDate,
          errorMessage: error instanceof Error && error.message
            ? error.message
            : "Impossible de charger les remorques du dispatch source.",
          isStaleSnapshot: false,
          isLoading: false,
          options: [],
          orderTruckIds: current.orderTruckIds,
          sourceChangeTruckPolicy: null,
        }));
      });

    return () => {
      abortController.abort();
    };
  }, [endOfDayLoadGroupTarget, endOfDayLoadOrderTarget, endOfDayLoadSourceDispatchDate]);

  React.useEffect(() => {
    if (editingOrderId && !orders.some((order) => order?.order_id === editingOrderId)) {
      clearOrderAddressValidationStates(editingOrderId);
      setEditingOrderId(null);
      setEditingOrderFocusKey(ORDER_DEFAULT_FOCUS_KEY);
    }
  }, [clearOrderAddressValidationStates, editingOrderId, orders]);

  React.useEffect(() => {
    const createdOrderId = pendingCreatedOrderIdRef.current;
    if (!createdOrderId) {
      return;
    }

    const createdOrder = orders.find((order) => order?.order_id === createdOrderId);
    if (!createdOrder) {
      return;
    }

    pendingCreatedOrderScrollIdRef.current = createdOrderId;
    pendingCreatedOrderIdRef.current = null;
  }, [orders]);

  React.useEffect(() => {
    const createdOrderId = pendingCreatedOrderScrollIdRef.current;
    if (!createdOrderId) {
      return undefined;
    }

    const animationFrameId = window.requestAnimationFrame(() => {
      const orderRows = Array.from(rootRef.current?.querySelectorAll("tr[data-order-id]") || []);
      const createdOrderRow = orderRows.find((row) => row.getAttribute("data-order-id") === createdOrderId);
      if (!createdOrderRow) {
        return;
      }

      createdOrderRow.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      pendingCreatedOrderScrollIdRef.current = null;
    });

    return () => window.cancelAnimationFrame(animationFrameId);
  }, [orders]);

  function updateNewOrderDraftValue(field, value, options = {}) {
    const nextDraftPatch = buildDeliveryClientDraftPatch(field, value, newOrderDraft, loadingClientOptionsByLookupKey);
    const touchesAddress = isOrderAddressField(field) || Object.prototype.hasOwnProperty.call(nextDraftPatch, "address");

    if (touchesAddress && options.preserveValidationToast !== true) {
      dismissAddressValidationToast();
    }

    if (touchesAddress) {
      clearAddressValidationState(getCreateAddressValidationKey("address"));
    }

    setNewOrderDraft((currentDraft) => ({
      ...currentDraft,
      ...buildCreateOrderBillToPatch(field, value, currentDraft.client_name, currentDraft.coop, loadingClientOptionsByLookupKey),
      ...buildDeliveryClientDraftPatch(field, value, currentDraft, loadingClientOptionsByLookupKey),
    }));
  }

  function updateNewOrderDeliveryValue(deliveryDraftId, field, value, options = {}) {
    const deliveryDraft = findCreateOrderDelivery(newOrderDraft, deliveryDraftId);
    if (!deliveryDraft) {
      return;
    }

    const nextDeliveryPatch = buildCreateDeliveryDraftPatch(
      field,
      value,
      deliveryDraft,
      loadingClientOptionsByLookupKey,
    );
    const touchesAddress = isOrderAddressField(field) || Object.prototype.hasOwnProperty.call(nextDeliveryPatch, "address");

    if (touchesAddress && options.preserveValidationToast !== true) {
      dismissAddressValidationToast();
    }

    if (touchesAddress) {
      clearAddressValidationState(getCreateAddressValidationKey(deliveryDraftId, "address"));
    }

    setNewOrderDraft((currentDraft) => ({
      ...currentDraft,
      ...buildCreateOrderBillToPatch(field, value, deliveryDraft.client_name, currentDraft.coop, loadingClientOptionsByLookupKey),
      deliveries: getCreateOrderDeliveries(currentDraft).map((delivery) => (
        delivery.draft_id === deliveryDraftId
          ? {
              ...delivery,
              ...buildCreateDeliveryDraftPatch(field, value, delivery, loadingClientOptionsByLookupKey),
            }
          : delivery
      )),
    }));
  }

  function addNewOrderDelivery() {
    dismissAddressValidationToast();
    setNewOrderDraft((currentDraft) => {
      const currentDeliveries = getCreateOrderDeliveries(currentDraft);
      const previousDelivery = currentDeliveries[currentDeliveries.length - 1] || null;

      return {
        ...currentDraft,
        deliveries: [
          ...currentDeliveries,
          createNextCreateOrderDelivery(previousDelivery),
        ],
      };
    });
  }

  function removeNewOrderDelivery(deliveryDraftId) {
    const deliveryDraft = findCreateOrderDelivery(newOrderDraft, deliveryDraftId);
    if (!deliveryDraft) {
      return;
    }

    dismissAddressValidationToast();
    clearAddressValidationState(getCreateAddressValidationKey(deliveryDraftId, "address"));
    setNewOrderDraft((currentDraft) => {
      const nextDeliveries = getCreateOrderDeliveries(currentDraft).filter((delivery) => delivery.draft_id !== deliveryDraftId);
      if (nextDeliveries.length === 0) {
        return currentDraft;
      }

      return {
        ...currentDraft,
        deliveries: nextDeliveries,
      };
    });
  }

  function updateOrderDraftValue(orderId, field, value, options = {}) {
    const sourceOrder = Array.isArray(orders) ? orders.find((item) => item?.order_id === orderId) : null;
    if (!sourceOrder) {
      return;
    }

    const sourceDraft = orderDrafts[orderId] || createEditableOrderDraft(sourceOrder);
    const nextDraftPatch = buildDeliveryClientDraftPatch(field, value, sourceDraft, loadingClientOptionsByLookupKey);
    const touchesAddress = isOrderAddressField(field) || Object.prototype.hasOwnProperty.call(nextDraftPatch, "address");

    if (touchesAddress && options.preserveValidationToast !== true) {
      dismissAddressValidationToast();
    }

    if (touchesAddress) {
      clearAddressValidationState(getOrderAddressValidationKey(orderId, "address"));
    }

    setOrderDrafts((currentDrafts) => ({
      ...currentDrafts,
      [orderId]: {
        ...(currentDrafts[orderId] || createEditableOrderDraft(sourceOrder)),
        ...buildDeliveryClientDraftPatch(
          field,
          value,
          currentDrafts[orderId] || createEditableOrderDraft(sourceOrder),
          loadingClientOptionsByLookupKey,
        ),
      },
    }));
  }

  function handleStartEditingOrder(order, focusKey = ORDER_DEFAULT_FOCUS_KEY) {
    void openEditingOrder(order, focusKey);
  }

  async function openEditingOrder(order, focusKey = ORDER_DEFAULT_FOCUS_KEY) {
    if (!canOpenGeneralOrderEdit(canEditOrders, order)) {
      return;
    }

    const isReady = await ensureClientCorrespondencesReady();
    if (!isReady) {
      return;
    }

    const resolvedFocusKey = typeof focusKey === "string" && focusKey ? focusKey : ORDER_DEFAULT_FOCUS_KEY;

    if (editingOrderId !== order.order_id) editingOrderSourceRef.current = order;

    if (editingOrderId && editingOrderId !== order.order_id) {
      clearOrderAddressValidationStates(editingOrderId);
    }

    setOrderDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };

      if (editingOrderId && editingOrderId !== order.order_id) {
        delete nextDrafts[editingOrderId];
      }

      nextDrafts[order.order_id] = nextDrafts[order.order_id] || createEditableOrderDraft(order);
      return nextDrafts;
    });
    setEditingOrderId(order.order_id);
    setEditingOrderFocusKey(resolvedFocusKey);
  }

  function handleCancelEditingOrder(orderId) {
    dismissAddressValidationToast();
    clearOrderAddressValidationStates(orderId);
    setOrderDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[orderId];
      return nextDrafts;
    });
    setEditingOrderId((currentOrderId) => (currentOrderId === orderId ? null : currentOrderId));
    setEditingOrderFocusKey(ORDER_DEFAULT_FOCUS_KEY);
  }

  React.useEffect(() => {
    if (!editingOrderId) {
      return undefined;
    }

    const handlePointerDownOutsideEditingRow = (event) => {
      const editingRow = rootRef.current?.querySelector('tr[data-order-row-editing="true"]');
      if (!editingRow || !(event.target instanceof Node) || editingRow.contains(event.target)) {
        return;
      }

      if (isPointerInsideConfirmationModal(event.target)) {
        return;
      }

      if (isLoadingClientSuggestionsTarget(event.target)) {
        return;
      }

      handleCancelEditingOrder(editingOrderId);
    };

    document.addEventListener("pointerdown", handlePointerDownOutsideEditingRow, true);
    return () => document.removeEventListener("pointerdown", handlePointerDownOutsideEditingRow, true);
  }, [editingOrderId]);

  async function handleCreateNextOrder() {
    if (!canCreate) {
      return;
    }

    let draftToCreate = {
      ...newOrderDraft,
      deliveries: getCreateOrderDeliveries(newOrderDraft),
      loading_client_name: coerceLoadingClientDraftValue(
        newOrderDraft.loading_client_name,
        loadingClientOptionsByLookupKey,
      ),
    };

    setLocalDispatchSaveNotice(null);
    dismissAddressValidationToast();
    clearCreateAddressValidationStates(draftToCreate);

    try {
      for (const delivery of draftToCreate.deliveries) {
        const resolvedAddress = isSelectedClientCanonicalAddress(
          delivery.client_name,
          delivery.address,
          loadingClientOptionsByLookupKey,
        )
          ? normalizeOptionalString(delivery.address)
          : await validateDraftAddressWithCivicCheck({
            fieldKey: "address",
            validationKey: getCreateAddressValidationKey(delivery.draft_id, "address"),
            value: delivery.address,
            onResolvedValue: (resolvedValue) => updateNewOrderDeliveryValue(
              delivery.draft_id,
              "address",
              resolvedValue,
              { preserveValidationToast: true }
            ),
            suppressSuccessToast: true,
          });

        draftToCreate = {
          ...draftToCreate,
          deliveries: draftToCreate.deliveries.map((currentDelivery) => (
            currentDelivery.draft_id === delivery.draft_id
              ? { ...currentDelivery, address: resolvedAddress,
                ...(validatedAddressOptionsRef.current[getCreateAddressValidationKey(delivery.draft_id, "address")]?.address === resolvedAddress
                  ? { allow_missing_civic_number: validatedAddressOptionsRef.current[getCreateAddressValidationKey(delivery.draft_id, "address")].allow_missing_civic_number } : {}) }
              : currentDelivery
          )),
        };
      }
    } catch (error) {
      if (isMissingCivicNumberConfirmationCancelledError(error)) {
        return;
      }
      return;
    }

    const createdOrderDeliveryDate = toDateInputValue(draftToCreate.delivery_date);
    const payloadsToCreate = buildCreatePayloads(draftToCreate);
    let createResult;
    try {
      createResult = typeof createOrders === "function"
        ? await createOrders({ orders: payloadsToCreate }, { skipSuccessToast: true })
        : await createOrder(payloadsToCreate[0], { skipSuccessToast: true });
    } catch {
      // The mutation hook owns error feedback; retain the draft on cancellation.
      return;
    }
    const createdOrderIds = Array.isArray(createResult?.created_order_ids)
      ? createResult.created_order_ids
        .map((value) => normalizeOptionalString(value))
        .filter(Boolean)
      : [normalizeOptionalString(createResult?.order?.order_id)].filter(Boolean);
    const createdOrderId = createdOrderIds[0] || "";
    const nextBrowserSearchQuery = createdOrderIds.length === 1 ? createdOrderId : "";
    setLocalDispatchSaveNotice({
      message: buildCreatedOrdersSaveNoticeMessage(createdOrderIds),
      tone: "success",
    });
    pendingCreatedOrderIdRef.current = createdOrderId || null;
    if (createdOrderDeliveryDate || nextBrowserSearchQuery) {
      setBrowserDeliveryDate(createdOrderDeliveryDate || "");
      onApplyOrderBrowser?.({
        deliveryDate: createdOrderDeliveryDate || "",
        searchQuery: nextBrowserSearchQuery,
      });
    }
    clearCreateAddressValidationStates(draftToCreate);
    clearDataManagerOrderCreateDraftState();
    setNewOrderDraft(createNewOrderDraft());
    setIsCreatingOrder(false);
  }

  function handleOpenCreateOrderDialog() {
    if (createBusy || !canCreate) {
      return;
    }

    setIsCreateOrderDialogOpen(true);
  }

  function handleConfirmCreateOrder() {
    setIsCreateOrderDialogOpen(false);
    void handleCreateNextOrder();
  }

  async function handleSaveOrder(order, options = {}) {
    // Keep the version from the start of editing when a background read updates the row.
    if (editingOrderSourceRef.current?.order_id === order.order_id) order = editingOrderSourceRef.current;
    const addressAlreadyValidated = options.addressAlreadyValidated === true;
    const draft = orderDrafts[order.order_id] || createEditableOrderDraft(order);
    const addressValidationError = addressValidationErrors[getOrderAddressValidationKey(order.order_id, "address")];
    const productError = getProductChoiceError(draft.grain_type, productCatalog, { originalValue: order.grain_type ?? "" });
    const draftErrors = {
      ...getOrderDraftErrors(draft, {
        clientCorrespondenceOptionsByLookupKey: loadingClientOptionsByLookupKey,
      }),
      ...(productError ? { grain_type: productError } : {}),
      ...(addressValidationError ? { address: addressValidationError } : {}),
    };
    if (hasDraftErrors(draftErrors) || !hasOrderDraftChanges(order, draft)) {
      const saveDisabledReason = resolveOrderSaveDisabledReason({
        draft,
        errors: draftErrors,
        order,
      });
      if (saveDisabledReason) {
        showToast({
          duration: 6000,
          title: "Enregistrement impossible",
          message: saveDisabledReason,
          type: "error",
        });
      }
      return;
    }

    dismissAddressValidationToast();
    clearOrderAddressValidationStates(order.order_id);

    let nextDraft = {
      ...draft,
      loading_client_name: resolveLoadingClientDraftValueForSave(
        order,
        draft,
        loadingClientOptionsByLookupKey,
      ),
    };
    let hasValidatedAddress = false;

    try {
      if (!addressAlreadyValidated && hasOrderAddressFieldChanged(order, nextDraft, "address")) {
        if (!isSelectedClientCanonicalAddress(
          nextDraft.client_name,
          nextDraft.address,
          loadingClientOptionsByLookupKey,
        )) {
          nextDraft = {
            ...nextDraft,
            address: await validateDraftAddressWithCivicCheck({
              fieldKey: "address",
              validationKey: getOrderAddressValidationKey(order.order_id, "address"),
              value: nextDraft.address,
              onResolvedValue: (resolvedValue) => updateOrderDraftValue(order.order_id, "address", resolvedValue, { preserveValidationToast: true }),
              suppressSuccessToast: true,
            }),
          };
          hasValidatedAddress = true;
        }
      }
    } catch (error) {
      if (isMissingCivicNumberConfirmationCancelledError(error)) {
        return;
      }
      return;
    }

    if (!hasOrderDraftChanges(order, nextDraft)) {
      handleCancelEditingOrder(order.order_id);
      return;
    }

    setLocalDispatchSaveNotice(null);
    try {
      const savedAddressOption = validatedAddressOptionsRef.current[getOrderAddressValidationKey(order.order_id, "address")];
      if (savedAddressOption?.address === nextDraft.address) {
        nextDraft.allow_missing_civic_number = savedAddressOption.allow_missing_civic_number;
      }
      await updateOrder(order.order_id, buildUpdatePayload(order, nextDraft), {
        skipSuccessToast: hasValidatedAddress,
      });
    } catch (error) {
      const frozenDispatchMessage = resolveFrozenDispatchMutationMessage(error);
      if (frozenDispatchMessage) {
        setLocalDispatchSaveNotice({
          message: frozenDispatchMessage,
          tone: "error",
        });
      }
      return;
    }
    clearOrderAddressValidationStates(order.order_id);
    setOrderDrafts((currentDrafts) => {
      const nextDrafts = { ...currentDrafts };
      delete nextDrafts[order.order_id];
      return nextDrafts;
    });
    setEditingOrderId((currentOrderId) => (currentOrderId === order.order_id ? null : currentOrderId));
    setEditingOrderFocusKey(ORDER_DEFAULT_FOCUS_KEY);
  }

  async function handleClearDeliveredOrderMutation(order) {
    if (typeof clearOrderDeliveredStatus !== "function") {
      return;
    }

    await clearOrderDeliveredStatus(order.order_id, buildDeletePayload(order));
    setClearDeliveredOrderTarget(null);
  }

  async function handleOrderExecutionMutation(payload) {
    if (!orderExecutionTarget?.order_id || typeof updateOrderExecution !== "function") {
      return;
    }

    setOrderExecutionErrorMessage("");
    try {
      await updateOrderExecution(orderExecutionTarget.order_id, payload);
      setOrderExecutionTarget(null);
    } catch (error) {
      setOrderExecutionErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "Impossible d'enregistrer l'exécution réelle de la commande.",
      );
    }
  }

  async function handleApplyEndOfDayLoadMutation() {
    if (
      typeof assignOrdersEndOfDayLoad !== "function"
      || !selectedEndOfDayLoadSourceTruck?.truckId
      || !endOfDayLoadSourceDispatchDate
      || !endOfDayLoadTargetDispatchDate
      || endOfDayLoadSourceValidationMessage
      || endOfDayLoadDateValidationMessage
      || isEndOfDayLoadSourceTrucksLoading
      || endOfDayLoadSourceTrucksErrorMessage
      || isEndOfDayLoadAssignmentUnchanged
      || isEndOfDayLoadPending
    ) {
      return;
    }

    setLocalDispatchSaveNotice(null);
    try {
      const deliveryMode = normalizeEndOfDayLoadDeliveryMode(endOfDayLoadDeliveryMode);
      const targetPeriod = resolveEndOfDayLoadTargetPeriod(deliveryMode);
      if (endOfDayLoadGroupTarget?.canApply) {
        const assignmentPayload = {
          dispatch_date: endOfDayLoadGroupTarget.dispatchDate,
          delivery_mode: deliveryMode,
          delivery_ids: endOfDayLoadGroupTarget.deliveryIds,
          order_ids: endOfDayLoadGroupTarget.orderIds,
          target_truck_id: selectedEndOfDayLoadSourceTruck.truckId,
          target_dispatch_date: endOfDayLoadTargetDispatchDate,
          target_period: targetPeriod,
          scope: "group",
          source_group_key: endOfDayLoadGroupTarget.sourceGroupKey,
          group_step_ids: endOfDayLoadGroupTarget.groupStepIds,
          source_dispatch_date: endOfDayLoadSourceDispatchDate,
          source_truck_id: selectedEndOfDayLoadSourceTruck.truckId,
        };
        if (endOfDayLoadGroupTarget.markerKey) {
          assignmentPayload.marker_key = endOfDayLoadGroupTarget.markerKey;
          assignmentPayload.marker_dispatch_date = endOfDayLoadGroupTarget.markerDispatchDate
            || endOfDayLoadGroupTarget.dispatchDate;
        }
        await assignOrdersEndOfDayLoad(assignmentPayload);
      } else if (endOfDayLoadOrderTarget?.order_id) {
        const deliveryIds = normalizePayloadStringList(endOfDayLoadOrderTarget.end_of_day_load_delivery_ids);
        const markerKey = normalizeOptionalString(endOfDayLoadOrderTarget.end_of_day_load_marker_key);
        const persistedSourceDispatchDate = normalizeOptionalString(endOfDayLoadOrderTarget.end_of_day_load_source_dispatch_date)
          || toDateInputValue(endOfDayLoadOrderTarget.delivery_date)
          || browserDeliveryDate;
        const markerDispatchDate = normalizeOptionalString(endOfDayLoadOrderTarget.end_of_day_load_marker_dispatch_date)
          || persistedSourceDispatchDate;
        const assignmentPayload = {
          dispatch_date: markerKey ? markerDispatchDate : persistedSourceDispatchDate,
          delivery_mode: deliveryMode,
          order_ids: [endOfDayLoadOrderTarget.order_id],
          source_dispatch_date: endOfDayLoadSourceDispatchDate,
          source_truck_id: selectedEndOfDayLoadSourceTruck.truckId,
          target_truck_id: selectedEndOfDayLoadSourceTruck.truckId,
          target_dispatch_date: endOfDayLoadTargetDispatchDate,
          target_period: targetPeriod,
        };
        if (deliveryIds.length > 0) {
          assignmentPayload.delivery_ids = deliveryIds;
        }
        if (markerKey) {
          assignmentPayload.marker_key = markerKey;
          assignmentPayload.marker_dispatch_date = markerDispatchDate;
        }
        await assignOrdersEndOfDayLoad({
          ...assignmentPayload,
        });
      } else {
        return;
      }
      setEndOfDayLoadOrderTarget(null);
      setEndOfDayLoadGroupTarget(null);
      setEndOfDayLoadSourceDispatchDate("");
      setEndOfDayLoadSourceTruckId("");
      setEndOfDayLoadTargetDispatchDate("");
      setEndOfDayLoadDeliveryMode("next_day");
    } catch (error) {
      const frozenDispatchMessage = resolveFrozenDispatchMutationMessage(error);
      if (frozenDispatchMessage) {
        setLocalDispatchSaveNotice({
          message: frozenDispatchMessage,
          tone: "error",
        });
      }
    } finally {
      setIsEndOfDayLoadLocalProcessing(false);
    }
  }

  const endOfDayLoadGroupRemovalOrders = React.useMemo(() => {
    if (!endOfDayLoadGroupTarget?.orderIds?.length) {
      return [];
    }
    const selectedOrderIds = new Set(endOfDayLoadGroupTarget.orderIds);
    return orders.filter((order) => selectedOrderIds.has(order?.order_id));
  }, [endOfDayLoadGroupTarget, orders]);
  const endOfDayLoadGroupOrderPreview = React.useMemo(() => {
    if (!endOfDayLoadGroupTarget?.orderIds?.length) {
      return "";
    }
    const orderedPreviewIds = endOfDayLoadGroupTarget.orderIds
      .map((orderId) => (
        endOfDayLoadGroupRemovalOrders.find((order) => order?.order_id === orderId)?.order_id
        || orderId
      ))
      .filter(Boolean);
    const preview = orderedPreviewIds.slice(0, 4).join(" · ");
    const extraCount = Math.max(0, orderedPreviewIds.length - 4);
    return `${preview}${extraCount > 0 ? ` · +${extraCount}` : ""}`;
  }, [endOfDayLoadGroupRemovalOrders, endOfDayLoadGroupTarget]);

  const canRemoveEndOfDayLoad = Boolean(
    typeof removeOrdersEndOfDayLoad === "function"
    && (
      (endOfDayLoadOrderTarget && isOrderEndOfDayLoadLocked(endOfDayLoadOrderTarget))
      || (
        endOfDayLoadGroupRemovalOrders.length > 0
        && endOfDayLoadGroupRemovalOrders.every((order) => isOrderEndOfDayLoadLocked(order))
      )
    ),
  );

  function buildEndOfDayLoadRemovalPayload(removalMode = "") {
    if (endOfDayLoadGroupTarget?.orderIds?.length > 0) {
      const markerKey = normalizeOptionalString(endOfDayLoadGroupRemovalOrders[0]?.end_of_day_load_marker_key);
      if (!markerKey) {
        return null;
      }
      const referenceOrder = endOfDayLoadGroupRemovalOrders[0] || null;
      const deliveryMode = resolveEndOfDayLoadDeliveryModeFromRecord(referenceOrder, endOfDayLoadGroupTarget.dispatchDate);
      const payload = {
        delivery_mode: deliveryMode,
        dispatch_date: endOfDayLoadGroupTarget.dispatchDate,
        group_step_ids: endOfDayLoadGroupTarget.groupStepIds,
        marker_dispatch_date: normalizeOptionalString(endOfDayLoadGroupRemovalOrders[0]?.end_of_day_load_marker_dispatch_date)
          || endOfDayLoadGroupTarget.dispatchDate,
        marker_key: markerKey,
        order_ids: endOfDayLoadGroupTarget.orderIds,
        scope: "group",
        source_dispatch_date: endOfDayLoadGroupTarget.dispatchDate,
        source_group_key: endOfDayLoadGroupTarget.sourceGroupKey,
        source_truck_id: endOfDayLoadGroupTarget.sourceTruckId,
        target_dispatch_date: endOfDayLoadGroupTarget.targetDispatchDate
          || endOfDayLoadTargetDispatchDate,
        target_period: resolveEndOfDayLoadTargetPeriod(deliveryMode),
      };
      if (removalMode) {
        payload.removal_mode = removalMode;
      }
      return payload;
    }

    if (!endOfDayLoadOrderTarget?.order_id) {
      return null;
    }

    const markerKey = normalizeOptionalString(endOfDayLoadOrderTarget.end_of_day_load_marker_key);
    if (!markerKey) {
      return null;
    }

    const sourceDispatchDate = normalizeOptionalString(endOfDayLoadOrderTarget.end_of_day_load_source_dispatch_date)
      || toDateInputValue(endOfDayLoadOrderTarget.delivery_date)
      || browserDeliveryDate;
    const deliveryMode = resolveEndOfDayLoadDeliveryModeFromRecord(endOfDayLoadOrderTarget, sourceDispatchDate);

    const payload = {
      delivery_mode: deliveryMode,
      dispatch_date: sourceDispatchDate,
      marker_dispatch_date: normalizeOptionalString(endOfDayLoadOrderTarget.end_of_day_load_marker_dispatch_date)
        || sourceDispatchDate,
      marker_key: markerKey,
      order_ids: [endOfDayLoadOrderTarget.order_id],
      source_dispatch_date: sourceDispatchDate,
      target_dispatch_date: normalizeOptionalString(endOfDayLoadOrderTarget.end_of_day_load_target_dispatch_date)
        || endOfDayLoadTargetDispatchDate,
      target_period: resolveEndOfDayLoadTargetPeriod(deliveryMode),
    };
    if (removalMode) {
      payload.removal_mode = removalMode;
    }
    return payload;
  }

  async function handleRemoveEndOfDayLoadMutation(removalMode = "") {
    if (typeof removeOrdersEndOfDayLoad !== "function" || !canRemoveEndOfDayLoad || isEndOfDayLoadRemovePending) {
      return;
    }

    const payload = buildEndOfDayLoadRemovalPayload(removalMode);
    if (!payload) {
      return;
    }

    setLocalDispatchSaveNotice(null);
    try {
      await removeOrdersEndOfDayLoad(payload);
      setIsEndOfDayLoadRemoveConfirmOpen(false);
      setEndOfDayLoadOrderTarget(null);
      setEndOfDayLoadGroupTarget(null);
      setEndOfDayLoadSourceDispatchDate("");
      setEndOfDayLoadSourceTruckId("");
      setEndOfDayLoadTargetDispatchDate("");
      setEndOfDayLoadDeliveryMode("next_day");
    } catch (error) {
      const frozenDispatchMessage = resolveFrozenDispatchMutationMessage(error);
      if (frozenDispatchMessage) {
        setLocalDispatchSaveNotice({
          message: frozenDispatchMessage,
          tone: "error",
        });
      }
    }
  }

  function handleApplyBrowserFilters() {
    onApplyOrderBrowser?.({
      deliveryDate: browserDeliveryDate,
      searchQuery,
    });
  }

  function handleClearOrderBrowserFilters() {
    setBrowserDeliveryDate("");
  }

  function handleQuickSelectGridOrderBrowseDate(nextValue) {
    setBrowserDeliveryDate(nextValue);
    onQuickSelectOrderBrowseDate?.(nextValue);
  }

  function handleDraftAdvancedFilterChange(fieldKey, nextValue) {
    setDraftAdvancedFilters((currentValue) => {
      const nextDraft = {
        ...currentValue,
        [fieldKey]: nextValue,
      };

      if ((fieldKey === "delivery_date_min" || fieldKey === "delivery_date_max") && nextValue) {
        nextDraft.scope = "browse";
      }

      return nextDraft;
    });
  }

  function handleApplyAdvancedFilters() {
    const nextFilters = pruneAdvancedFiltersForVisibleKeys(draftAdvancedFilters, visibleFilterKeys);
    const hasDeliveryDateRange = isDeliveryDateRangeFilterActive(nextFilters);

    if (hasDeliveryDateRange) {
      onRequireFullOrdersCatalog?.(true);
      setSortState({ direction: "asc", key: "delivery_date" });
      onApplyOrderBrowser?.({
        deliveryDate: "",
        searchQuery,
      });
    } else {
      onRequireFullOrdersCatalog?.(false);
      const nextScope = draftAdvancedFilters.scope === "browse" ? "browse" : "dispatch";
      if (nextScope === "browse" && !isBrowseMode) {
        onApplyOrderBrowser?.({
          deliveryDate: orderBrowseDateFilter || browserDeliveryDate,
          searchQuery,
        });
      } else if (nextScope === "dispatch" && isBrowseMode) {
        onResetOrderScope?.();
      }
    }

    setAdvancedFilters(nextFilters);
    setIsAdvancedFiltersOpen(false);
  }

  function handleResetAdvancedFiltersDraft() {
    setDraftAdvancedFilters({
      ...EMPTY_ORDER_ADVANCED_FILTERS,
      scope: isBrowseMode ? "browse" : "dispatch",
    });
  }

  function handleRemoveActiveFilter(filterKey) {
    if (filterKey === "search") {
      onSearchQueryChange?.("");
      return;
    }

    if (filterKey === "date") {
      onQuickSelectOrderBrowseDate?.("");
      return;
    }

    if (filterKey === "scope") {
      onResetOrderScope?.();
      return;
    }

    if (filterKey === "pm") {
      setIsPmOnlyFilter(false);
      return;
    }

    if (filterKey === "delivery_date") {
      onRequireFullOrdersCatalog?.(false);
    }

    setAdvancedFilters((currentValue) => clearAdvancedFilterValue(currentValue, filterKey));
    setDraftAdvancedFilters((currentValue) => clearAdvancedFilterValue(currentValue, filterKey));
  }

  function handleClearAllFilters() {
    onSearchQueryChange?.("");
    setIsPmOnlyFilter(false);
    onRequireFullOrdersCatalog?.(false);
    setAdvancedFilters({ ...EMPTY_ORDER_ADVANCED_FILTERS });
    setDraftAdvancedFilters({
      ...EMPTY_ORDER_ADVANCED_FILTERS,
      scope: isBrowseMode ? "browse" : "dispatch",
    });
    if (isBrowseMode || orderBrowseDateFilter) {
      onResetOrderScope?.();
    }
  }

  function handleClearSearchQuery() {
    onSearchQueryChange?.("");
  }

  function handleToggleAdvancedFilters(nextOpen) {
    if (nextOpen) {
      setDraftAdvancedFilters({
        ...EMPTY_ORDER_ADVANCED_FILTERS,
        ...advancedFilters,
        scope: isBrowseMode ? "browse" : "dispatch",
      });
      setDraftVisibleFilterKeys(visibleFilterKeys);
      setIsCustomizingFilters(false);
    }
    setIsAdvancedFiltersOpen(nextOpen);
  }

  function handleToggleFilterSettings(nextValue) {
    const shouldOpenSettings = Boolean(nextValue);
    if (shouldOpenSettings) {
      setDraftVisibleFilterKeys(visibleFilterKeys);
    }
    setIsCustomizingFilters(shouldOpenSettings);
  }

  function handleToggleVisibleFilter(filterKey, isSelected) {
    setDraftVisibleFilterKeys((currentKeys) => {
      const normalizedKeys = normalizeVisibleFilterKeys(currentKeys);
      if (isSelected) {
        return normalizeVisibleFilterKeys([...normalizedKeys, filterKey]);
      }
      return normalizedKeys.filter((key) => key !== filterKey);
    });
  }

  function handleResetFilterSettings() {
    setDraftVisibleFilterKeys([...ORDER_DEFAULT_ADVANCED_FILTER_KEYS]);
  }

  function handleSaveFilterSettings() {
    const nextVisibleFilterKeys = normalizeVisibleFilterKeys(draftVisibleFilterKeys);
    setVisibleFilterKeys(nextVisibleFilterKeys);
    setAdvancedFilters((currentValue) => pruneAdvancedFiltersForVisibleKeys(currentValue, nextVisibleFilterKeys));
    setDraftAdvancedFilters((currentValue) => pruneAdvancedFiltersForVisibleKeys(currentValue, nextVisibleFilterKeys));
    setIsCustomizingFilters(false);

    if (typeof saveColumnPreferences === "function") {
      const saveRevision = ++filterPreferenceSaveRevisionRef.current;
      const saveOperation = filterPreferenceSaveChainRef.current
        .catch(() => undefined)
        .then(() => saveColumnPreferences(ORDER_FILTERS_PREFERENCE_KEY, {
          columnOrder: nextVisibleFilterKeys,
          columnWidths: {},
        }));
      filterPreferenceSaveChainRef.current = saveOperation;
      void saveOperation.then((preferences) => {
        const confirmedKeys = resolveVisibleOrderFilterKeys(preferences);
        confirmedVisibleFilterKeysRef.current = [...confirmedKeys];
        if (saveRevision === filterPreferenceSaveRevisionRef.current) {
          setVisibleFilterKeys(confirmedKeys);
          setDraftVisibleFilterKeys(confirmedKeys);
        }
      }).catch(() => {
        if (saveRevision === filterPreferenceSaveRevisionRef.current) {
          const confirmedKeys = [...confirmedVisibleFilterKeysRef.current];
          setVisibleFilterKeys(confirmedKeys);
          setDraftVisibleFilterKeys(confirmedKeys);
          showToast({
            duration: 6000,
            title: "Filtres non enregistrés",
            message: "La dernière configuration synchronisée a été restaurée.",
            type: "error",
          });
        }
      });
    }
  }

  function handleOpenExportPdfDialog() {
    if (sortedOrders.length === 0) {
      showToast({
        duration: 5000,
        title: "Export PDF",
        message: "Aucune commande à exporter dans la sélection courante.",
        type: "info",
      });
      return;
    }

    setPdfSelectedColumnKeys(committedPdfColumnKeys);
    setPdfColumnLabels(committedPdfColumnLabels);
    setIsPdfDialogOpen(true);
  }

  function handleTogglePdfColumn(columnKey, isSelected) {
    setPdfSelectedColumnKeys((currentKeys) => {
      const currentSet = new Set(currentKeys);
      if (isSelected) {
        currentSet.add(columnKey);
      } else {
        currentSet.delete(columnKey);
      }
      return ORDER_PDF_COLUMN_CATALOG
        .map((column) => column.key)
        .filter((key) => currentSet.has(key));
    });
  }

  function handlePdfColumnLabelChange(columnKey, nextValue) {
    setPdfColumnLabels((currentLabels) => ({
      ...currentLabels,
      [columnKey]: nextValue,
    }));
  }

  function handleResetPdfDialog() {
    setPdfSelectedColumnKeys([...ORDER_PDF_DEFAULT_COLUMN_KEYS]);
    setPdfColumnLabels(resolveOrderPdfColumnLabels(null));
  }

  function handleConfirmExportPdf() {
    const normalizedPdfLabels = normalizeOrderPdfColumnLabels(pdfColumnLabels);
    const exportColumns = buildOrderPdfExportColumns(pdfSelectedColumnKeys, normalizedPdfLabels);
    const exportViewModel = buildDataManagerOrdersExportViewModel({
      activeFilters: buildOrdersActiveFilters({
        advancedFilters,
        isBrowseMode,
        isPmOnlyFilter,
        orderBrowseDateFilter,
        searchQuery,
      }),
      exportColumns,
      rows: sortedOrders,
      scopeLabel: buildOrdersScopeLabel({
        currentDispatchDates,
        isBrowseMode,
        orderBrowseDateFilter,
      }),
      searchQuery,
      sortState,
    });

    if (exportViewModel.resultCount === 0) {
      showToast({
        duration: 5000,
        title: "Export PDF",
        message: "Aucune commande à exporter dans la sélection courante.",
        type: "info",
      });
      return;
    }

    setIsPdfExporting(true);
    void (async () => {
      let documentWindow = null;
      try {
        documentWindow = openExportDocumentWindow({
          detail: `${exportViewModel.resultCount} commande(s) · ${exportViewModel.scopeLabel}`,
          eyebrow: "Data Manager",
          message: "Nous préparons le PDF des commandes affichées dans la grille.",
          preferredWidth: 1240,
          title: "Préparation du PDF Commandes",
          windowName: "grain-data-manager-orders-pdf",
        });

        await downloadDataManagerOrdersPdf({
          documentWindow,
          exportViewModel,
        });

        setCommittedPdfColumnKeys(pdfSelectedColumnKeys);
        setCommittedPdfColumnLabels(normalizedPdfLabels);
        setIsPdfDialogOpen(false);

        if (typeof saveColumnPreferences === "function") {
          const saveRevision = ++pdfPreferenceSaveRevisionRef.current;
          const saveOperation = pdfPreferenceSaveChainRef.current
            .catch(() => undefined)
            .then(() => saveColumnPreferences(ORDER_PDF_PREFERENCE_KEY, {
              columnLabels: buildPersistedOrderPdfColumnLabels(normalizedPdfLabels),
              columnOrder: pdfSelectedColumnKeys,
              columnWidths: {},
            }));
          pdfPreferenceSaveChainRef.current = saveOperation;
          void saveOperation.then((preferences) => {
            const confirmedColumnKeys = resolveOrderPdfColumnKeys(preferences);
            const confirmedColumnLabels = resolveOrderPdfColumnLabels(preferences);
            confirmedPdfPreferencesRef.current = {
              columnKeys: [...confirmedColumnKeys],
              columnLabels: { ...confirmedColumnLabels },
            };
            if (saveRevision === pdfPreferenceSaveRevisionRef.current) {
              setCommittedPdfColumnKeys(confirmedColumnKeys);
              setCommittedPdfColumnLabels(confirmedColumnLabels);
            }
          }).catch(() => {
            if (saveRevision === pdfPreferenceSaveRevisionRef.current) {
              const confirmedPreferences = confirmedPdfPreferencesRef.current;
              setCommittedPdfColumnKeys([...confirmedPreferences.columnKeys]);
              setCommittedPdfColumnLabels({ ...confirmedPreferences.columnLabels });
              setPdfSelectedColumnKeys([...confirmedPreferences.columnKeys]);
              setPdfColumnLabels({ ...confirmedPreferences.columnLabels });
              showToast({
                duration: 6000,
                title: "Configuration PDF non enregistrée",
                message: "Le PDF est généré; la dernière configuration synchronisée a été restaurée.",
                type: "error",
              });
            }
          });
        }

        showToast({
          duration: 5000,
          title: "Export PDF",
          message: `${exportViewModel.resultCount} commande(s) exportée(s) · le PDF s’ouvre dans une nouvelle fenêtre.`,
          type: "success",
        });
      } catch (error) {
        const errorMessage = isExportApiError(error)
          ? error.message
          : error instanceof Error && error.message
            ? error.message
            : "Le PDF des commandes n’a pas pu être généré.";

        if (documentWindow) {
          markExportDocumentWindowFailed(documentWindow, {
            detail: errorMessage,
            eyebrow: "Data Manager",
            message: "Le PDF des commandes n’a pas pu être généré.",
            title: "Export interrompu",
          });
        }

        showToast({
          duration: 8000,
          title: "Export PDF",
          message: errorMessage,
          type: "error",
        });
      } finally {
        setIsPdfExporting(false);
      }
    })();
  }

  function handleOrderSelectionChange(orderId, isSelected) {
    if (!orderId) {
      return;
    }

    setSelectedOrderIds((currentSelection) => {
      if (isSelected) {
        if (currentSelection[orderId]) {
          return currentSelection;
        }
        return {
          ...currentSelection,
          [orderId]: true,
        };
      }

      if (!currentSelection[orderId]) {
        return currentSelection;
      }

      const nextSelection = { ...currentSelection };
      delete nextSelection[orderId];
      return nextSelection;
    });
  }

  function handleToggleAllVisibleOrderSelections(shouldSelect) {
    if (selectableVisibleOrders.length === 0) {
      return;
    }

    setSelectedOrderIds((currentSelection) => {
      if (!shouldSelect) {
        const nextSelection = { ...currentSelection };
        selectableVisibleOrders.forEach((order) => {
          delete nextSelection[order.order_id];
        });
        return nextSelection;
      }

      const nextSelection = { ...currentSelection };
      selectableVisibleOrders.forEach((order) => {
        nextSelection[order.order_id] = true;
      });
      return nextSelection;
    });
  }

  async function handleConfirmDeleteSelectedOrders() {
    const ordersToDelete = [...selectedVisibleDeleteOrders];
    if (ordersToDelete.length === 0 || typeof deleteOrder !== "function" || (!canBulkDeleteOrders && ordersToDelete.length > 1)) {
      setIsDeleteSelectionDialogOpen(false);
      return;
    }

    setIsDeleteSelectionDialogOpen(false);
    setIsDeletingSelection(true);

    let deletedCount = 0;
    let dispatchWarning = false;
    const failures = [];
    // A single deletion reports its Dispatch update in its own progress toast.
    const reportsEachDeletion = ordersToDelete.length === 1;

    for (const order of ordersToDelete) {
      try {
        const result = await deleteOrder(order.order_id, buildDeletePayload(order), {
          shouldReload: false,
          skipSuccessToast: !reportsEachDeletion,
        });
        deletedCount += 1;
        dispatchWarning ||= result?._dispatch_warning === true;
      } catch (error) {
        failures.push({
          message: resolveMutationErrorMessage(error, `Impossible de supprimer la commande ${order.order_id}.`),
          orderId: order.order_id,
        });
      }
    }

    if (deletedCount > 0) {
      await refreshState?.();
    }

    setSelectedOrderIds({});
    setIsDeletingSelection(false);

    if (deletedCount > 0 && failures.length === 0) {
      if (reportsEachDeletion) return;
      showToast({
        duration: dispatchWarning ? 12000 : 6000,
        title: deletedCount > 1 ? "Commandes supprimées" : "Commande supprimée",
        message: `${deletedCount} commande(s) supprimée(s).${dispatchWarning ? " Dispatch à vérifier : consultez Mise à jour." : ""}`,
        type: dispatchWarning ? "warning" : "success",
      });
      return;
    }

    if (deletedCount > 0 && failures.length > 0) {
      showToast({
        duration: 9000,
        title: "Suppression partielle",
        message: `${deletedCount} commande(s) supprimée(s), ${failures.length} en erreur. ${failures[0].message}`,
        type: "error",
      });
      return;
    }

    // A single deletion already reported its error in its progress toast.
    if (failures.length > 0 && !reportsEachDeletion) {
      showToast({
        duration: 9000,
        title: "Suppression impossible",
        message: failures[0].message,
        type: "error",
      });
    }
  }

  function handleApplyBrowserFiltersFromDialog() {
    handleApplyBrowserFilters();
  }

  function handleClearOrderBrowserFiltersFromDialog() {
    handleClearOrderBrowserFilters();
  }

  function handleGridToggleAllVisibleOrderSelections(nextValue) {
    if (nextValue === "delete-selection-dialog") {
      setIsDeleteSelectionDialogOpen(true);
      return;
    }

    handleToggleAllVisibleOrderSelections(nextValue);
  }

  // Numéro de référence du chargement : enregistré seul, puis la grille se recharge ; Consolidation ouverte ailleurs suit.
  async function handleGridSaveLoadingReference(order, reference) {
    try {
      await updateOrderLoadingReference(order.order_id, reference);
    } catch (error) {
      showToast({ title: `N° de référence · ${order.order_id}`, message: resolveMutationErrorMessage(error, "Impossible d'enregistrer le numéro de référence."), type: "error" });
      throw error;
    }
    publishOrderDataChange("data-manager");
    await refreshState?.();
  }

  function handleGridClearDeliveredOrder(order) {
    if (!canClearDeliveredOrders || !order?.can_clear_delivered) {
      return;
    }

    setClearDeliveredOrderTarget(order);
  }

  function handleGridEditOrderExecution(order) {
    if (!order?.order_id || isOrderBillingApproved(order)) {
      return;
    }

    setOrderExecutionErrorMessage("");
    setOrderExecutionTarget(order);
    if (canWriteOrderDeliveryDriver && !order?.is_cancelled) {
      void ensureEndOfDayLoadTrucksReady();
    }
  }

  function handleCloseOrderExecutionDialog() {
    if (orderExecutionPending) {
      return;
    }
    setOrderExecutionErrorMessage("");
    setOrderExecutionTarget(null);
  }

  function handleSubmitOrderExecution(payload) {
    void handleOrderExecutionMutation(payload);
  }

  function openConsolidation(orders) {
    if (!canOpenConsolidation) return;
    onOpenConsolidation?.(orders, { selectedOrderIds, sortState, advancedFilters, isPmOnlyFilter,
      scroll: captureOrdersScroll(rootRef.current) });
  }

  function handleGridApproveForBilling(order) { openConsolidation([order]); }

  function handleOpenBulkBillingApproval() { openConsolidation(selectedVisibleOrders); }

  function handleGridAssignEndOfDayLoad(order) {
    if (typeof assignOrdersEndOfDayLoad !== "function" || !order?.order_id) {
      return;
    }

    void openEndOfDayLoadDialogForOrder(order);
  }

  async function openEndOfDayLoadDialogForOrder(order) {
    const loadedTrucks = await ensureEndOfDayLoadTrucksReady();
    if (loadedTrucks === null) {
      return;
    }

    setLocalDispatchSaveNotice(null);
    setIsEndOfDayLoadRemoveConfirmOpen(false);
    const groupAction = resolveEndOfDayLoadGroupActionFromOrder(order, orders);
    if (groupAction?.canApply) {
      setEndOfDayLoadOrderTarget(null);
      const selectedOrders = orders.filter((currentOrder) => groupAction.orderIds?.includes(currentOrder?.order_id));
      const sourceOrders = selectedOrders.length > 0 ? selectedOrders : [order];
      const sourceDispatchDate = groupAction.sourceDispatchDate || groupAction.dispatchDate;
      const referenceOrder = sourceOrders[0] || order;
      const deliveryMode = resolveEndOfDayLoadDeliveryModeFromRecord(referenceOrder, sourceDispatchDate);
      const initialTargetDispatchDate = resolveEndOfDayLoadTargetDispatchDateForMode(
        sourceDispatchDate,
        deliveryMode,
        groupAction.targetDispatchDate,
      );
      setEndOfDayLoadSourceDispatchDate(sourceDispatchDate);
      setEndOfDayLoadSourceTruckId(groupAction.sourceTruckId || "");
      setEndOfDayLoadDeliveryMode(deliveryMode);
      endOfDayLoadDayTargetDispatchDateRef.current = initialTargetDispatchDate;
      setEndOfDayLoadTargetDispatchDate(initialTargetDispatchDate);
      setEndOfDayLoadGroupTarget(groupAction);
      return;
    }

    setEndOfDayLoadGroupTarget(null);
    const sourceDispatchDate = normalizeOptionalString(order?.end_of_day_load_source_dispatch_date)
      || toDateInputValue(order?.delivery_date)
      || browserDeliveryDate;
    const sourceTruckId = normalizeOptionalString(order?.end_of_day_load_source_truck_id)
      || normalizeOptionalString(order?.end_of_day_load_candidate_source_truck_id)
      || "";
    const deliveryMode = resolveEndOfDayLoadDeliveryModeFromRecord(order, sourceDispatchDate);
    const existingTargetDispatchDate = normalizeOptionalString(order?.end_of_day_load_target_dispatch_date);
    setEndOfDayLoadSourceDispatchDate(sourceDispatchDate);
    setEndOfDayLoadSourceTruckId(sourceTruckId);
    setEndOfDayLoadDeliveryMode(deliveryMode);
    const initialTargetDispatchDate = resolveEndOfDayLoadTargetDispatchDateForMode(
      sourceDispatchDate,
      deliveryMode,
      existingTargetDispatchDate,
    );
    endOfDayLoadDayTargetDispatchDateRef.current = initialTargetDispatchDate;
    setEndOfDayLoadTargetDispatchDate(initialTargetDispatchDate);
    setEndOfDayLoadOrderTarget(order);
  }

  function handleOpenEndOfDayLoadGroupDialog() {
    if (!selectedEndOfDayLoadGroupAction.canApply) {
      return;
    }

    void openEndOfDayLoadDialogForGroup(selectedEndOfDayLoadGroupAction);
  }

  async function openEndOfDayLoadDialogForGroup(groupAction) {
    const loadedTrucks = await ensureEndOfDayLoadTrucksReady();
    if (loadedTrucks === null || !groupAction?.canApply) {
      return;
    }

    setLocalDispatchSaveNotice(null);
    setIsEndOfDayLoadRemoveConfirmOpen(false);
    setEndOfDayLoadOrderTarget(null);
    const selectedOrders = orders.filter((currentOrder) => groupAction.orderIds?.includes(currentOrder?.order_id));
    const referenceOrder = selectedOrders[0] || null;
    const sourceDispatchDate = groupAction.sourceDispatchDate || groupAction.dispatchDate;
    const deliveryMode = resolveEndOfDayLoadDeliveryModeFromRecord(referenceOrder, sourceDispatchDate);
    setEndOfDayLoadSourceDispatchDate(sourceDispatchDate);
    setEndOfDayLoadSourceTruckId(groupAction.sourceTruckId || "");
    setEndOfDayLoadDeliveryMode(deliveryMode);
    const initialTargetDispatchDate = resolveEndOfDayLoadTargetDispatchDateForMode(
      sourceDispatchDate,
      deliveryMode,
      groupAction.targetDispatchDate,
    );
    endOfDayLoadDayTargetDispatchDateRef.current = initialTargetDispatchDate;
    setEndOfDayLoadTargetDispatchDate(initialTargetDispatchDate);
    setEndOfDayLoadGroupTarget(groupAction);
  }

  function handleGridSaveOrder(order) {
    void handleSaveOrder(order);
  }

  function handleCloseCreateOrderDialog() {
    setIsCreateOrderDialogOpen(false);
  }

  function handleCloseDeleteSelectionDialog() {
    setIsDeleteSelectionDialogOpen(false);
  }

  function handleCloseClearDeliveredDialog() {
    if (clearDeliveredPending) {
      return;
    }

    setClearDeliveredOrderTarget(null);
  }

  function handleCloseEndOfDayLoadDialog() {
    if (isEndOfDayLoadPending || isEndOfDayLoadRemovePending) {
      return;
    }

    setIsEndOfDayLoadRemoveConfirmOpen(false);
    setEndOfDayLoadOrderTarget(null);
    setEndOfDayLoadGroupTarget(null);
    setEndOfDayLoadSourceDispatchDate("");
    setEndOfDayLoadSourceTruckId("");
    setEndOfDayLoadTargetDispatchDate("");
    endOfDayLoadDayTargetDispatchDateRef.current = "";
    setEndOfDayLoadDeliveryMode("next_day");
  }

  function handleConfirmDeleteSelectionDialog() {
    void handleConfirmDeleteSelectedOrders();
  }

  function handleConfirmClearDeliveredDialog() {
    if (!clearDeliveredOrderTarget) {
      return;
    }

    void handleClearDeliveredOrderMutation(clearDeliveredOrderTarget);
  }

  function handleConfirmEndOfDayLoadDialog() {
    void handleApplyEndOfDayLoadMutation();
  }

  function handleOpenEndOfDayLoadRemoveConfirm() {
    if (!canRemoveEndOfDayLoad) {
      return;
    }

    setIsEndOfDayLoadRemoveConfirmOpen(true);
  }

  function handleCancelEndOfDayLoadRemoveConfirm() {
    if (isEndOfDayLoadRemovePending) {
      return;
    }

    setIsEndOfDayLoadRemoveConfirmOpen(false);
  }

  function handleConfirmEndOfDayLoadRemoveDialog(removalMode) {
    void handleRemoveEndOfDayLoadMutation(removalMode);
  }

  function handleStartCreatingOrderPanel() {
    void startCreatingOrderPanel();
  }

  async function startCreatingOrderPanel() {
    const isReady = await ensureClientCorrespondencesReady();
    if (!isReady) {
      return;
    }

    clearCreateAddressValidationStates();
    setLocalDispatchSaveNotice(null);
    setNewOrderDraft((currentDraft) => ({
      ...currentDraft,
      order_date: formatDateInput(new Date()),
    }));
    setIsCreatingOrder(true);
  }

  function handleStopCreatingOrderPanel() {
    setIsCreateOrderDialogOpen(false);
    clearCreateAddressValidationStates(newOrderDraft);
    setIsCreatingOrder(false);
  }

  const createOrderPanelProps = {
    canAddOrder: canEditOrders,
    toolbar,
    productCatalog,
    productCodes,
    addNewOrderDelivery,
    canCreate,
    createAddressValidationPending,
    createBusy,
    createDeliveryCount,
    createDisabledReason,
    createDisabledMessages,
    createLoadingAddressPreview,
    createOrderDeliveries,
    createPending,
    handleCreateAddressEnterValidation,
    handleOpenCreateOrderDialog,
    handleStartCreatingOrder: handleStartCreatingOrderPanel,
    handleStopCreatingOrder: handleStopCreatingOrderPanel,
    isCreatingOrder,
    loadingClientOptions,
    loadingClientOptionsByLookupKey,
    newOrderDraft,
    registerAddressValidationFieldRef,
    removeNewOrderDelivery,
    updateNewOrderDeliveryValue,
    updateNewOrderDraftValue,
    visibleNewOrderErrors,
  };

  const resultSummaryLabel = React.useMemo(
    () => {
      if (isPendingBillingApprovalFilter) {
        return "File globale · À approuver";
      }
      const deliveryDateRangeChip = buildOrderAdvancedFilterChips(advancedFilters)
        .find((chip) => chip.key === "delivery_date");
      if (deliveryDateRangeChip) {
        return `Période ${deliveryDateRangeChip.value}`;
      }

      return buildOrdersScopeLabel({
        currentDispatchDates,
        isBrowseMode,
        orderBrowseDateFilter,
      });
    },
    [advancedFilters, currentDispatchDates, isBrowseMode, isPendingBillingApprovalFilter, orderBrowseDateFilter],
  );

  const deliveryDatePickerValue = React.useMemo(
    () => resolveOrderBrowserDeliveryDate({
      currentDispatchDates,
      isBrowseMode,
      orderBrowseDateFilter,
    }),
    [currentDispatchDates, isBrowseMode, orderBrowseDateFilter],
  );

  const activeFilterChips = React.useMemo(
    () => buildOrdersActiveFilters({
      advancedFilters,
      isBrowseMode,
      isPmOnlyFilter,
      orderBrowseDateFilter,
      searchQuery,
    }),
    [advancedFilters, isBrowseMode, isPmOnlyFilter, orderBrowseDateFilter, searchQuery],
  );

  const hasAdvancedFiltersDraft = React.useMemo(
    () => Boolean(
      hasAdvancedFilterValue(draftAdvancedFilters, visibleFilterKeys)
      || (draftAdvancedFilters.scope === "browse") !== isBrowseMode
    ),
    [draftAdvancedFilters, isBrowseMode, visibleFilterKeys],
  );

  const orderGridSortHandlers = {
    resizePointerDown: handleOrderColumnResizePointerDown,
    sort: handleSort,
  };

  const orderGridInteractionProps = {
    activeFilterChips,
    advancedFilters,
    draftVisibleFilterKeys,
    draftAdvancedFilters,
    hasAdvancedFiltersDraft,
    isAdvancedFiltersOpen,
    isCustomizingFilters,
    isRecentBrowseLimited,
    onApplyAdvancedFilters: handleApplyAdvancedFilters,
    onApproveForBilling: handleGridApproveForBilling,
    onClearAllFilters: handleClearAllFilters,
    onClearSearchQuery: handleClearSearchQuery,
    onDraftAdvancedFilterChange: handleDraftAdvancedFilterChange,
    onExportPdf: canExportOrders ? handleOpenExportPdfDialog : null,
    deleteSelectionBlockedReason: !canBulkDeleteOrders && selectedDeleteCount > 1
      ? "Supprimer plusieurs commandes à la fois demande le droit « Supprimer plusieurs commandes »."
      : "",
    onResetFilterSettings: handleResetFilterSettings,
    onOpenEndOfDayLoadGroupDialog: handleOpenEndOfDayLoadGroupDialog,
    onOpenBulkBillingApproval: handleOpenBulkBillingApproval,
    onOpenOrderBrowser,
    onQuickSelectOrderBrowseDate: handleQuickSelectGridOrderBrowseDate,
    onRemoveActiveFilter: handleRemoveActiveFilter,
    onResetAdvancedFilters: handleResetAdvancedFiltersDraft,
    onResetOrderScope,
    onSaveFilterSettings: handleSaveFilterSettings,
    onSearchQueryChange,
    onSort: orderGridSortHandlers,
    onColumnMove: orderColumnPrefs.handleColumnMove,
    onColumnResizeEnd: orderColumnPrefs.handleResizeEnd,
    resolvedOrderColumns: orderColumnPrefs.resolvedColumns,
    resultSummaryLabel,
    searchInputRef,
    onToggleAdvancedFilters: handleToggleAdvancedFilters,
    onToggleAllVisibleOrderSelections: handleGridToggleAllVisibleOrderSelections,
    onToggleFilterSettings: handleToggleFilterSettings,
    onTogglePendingBillingApprovalFilter,
    onToggleVisibleFilter: handleToggleVisibleFilter,
    visibleFilterKeys,
    onCancelEditingOrder: handleCancelEditingOrder,
    onAssignEndOfDayLoad: handleGridAssignEndOfDayLoad,
    onOrderAddressEnterValidation: handleOrderAddressEnterValidation,
    onOrderSelectionChange: handleOrderSelectionChange,
    onClearDeliveredOrder: handleGridClearDeliveredOrder,
    onSaveLoadingReference: handleGridSaveLoadingReference,
    onEditOrderExecution: handleGridEditOrderExecution,
    onSaveOrder: handleGridSaveOrder,
    onStartEditingOrder: handleStartEditingOrder,
    onUpdateOrderDraftValue: updateOrderDraftValue,
    focusedOrderId,
    onFocusOrderRow: setFocusedOrderId,
  };

  const orderBrowserDialogProps = {
    browserDeliveryDate,
    canApplyBrowserFilters,
    dispatchDateSummary,
    onApplyBrowserFilters: handleApplyBrowserFiltersFromDialog,
    onBrowserDeliveryDateChange: setBrowserDeliveryDate,
    onClearBrowserFilters: handleClearOrderBrowserFiltersFromDialog,
    onClose: onCloseOrderBrowser,
    totalOrderCount,
  };

  const orderPdfDialogProps = {
    columnLabels: pdfColumnLabels,
    columns: ORDER_PDF_COLUMN_CATALOG,
    isExporting: isPdfExporting,
    onClose: () => {
      if (!isPdfExporting) {
        setIsPdfDialogOpen(false);
      }
    },
    onExport: handleConfirmExportPdf,
    onLabelChange: handlePdfColumnLabelChange,
    onReset: handleResetPdfDialog,
    onToggleColumn: handleTogglePdfColumn,
    resultSummaryLabel,
    selectedColumnKeys: pdfSelectedColumnKeys,
    visibleOrderCount,
  };

  const createOrderDialogConfig = {
    isOpen: isCreateOrderDialogOpen,
    onClose: handleCloseCreateOrderDialog,
    onConfirm: handleConfirmCreateOrder,
    summaryItems: createOrderConfirmationSummaryItems,
  };

  const deleteSelectionDialogConfig = {
    canBulkDeleteOrders,
    isDeletingSelection,
    isOpen: showBulkDeleteControls && isDeleteSelectionDialogOpen,
    onClose: handleCloseDeleteSelectionDialog,
    onConfirm: handleConfirmDeleteSelectionDialog,
    selectedCount: selectedDeleteCount,
  };

  const clearDeliveredDialogConfig = {
    isOpen: Boolean(clearDeliveredOrderTarget),
    isPending: Boolean(clearDeliveredPending),
    onClose: handleCloseClearDeliveredDialog,
    onConfirm: handleConfirmClearDeliveredDialog,
    order: clearDeliveredOrderTarget,
  };

  const endOfDayLoadRemovalReferenceOrder = endOfDayLoadGroupRemovalOrders[0] || endOfDayLoadOrderTarget || null;
  const endOfDayLoadRemovalSourceTruckId = endOfDayLoadGroupTarget?.sourceTruckId
    || normalizeOptionalString(endOfDayLoadRemovalReferenceOrder?.end_of_day_load_source_truck_id);
  const endOfDayLoadRemovalTargetTruckId = endOfDayLoadGroupTarget?.targetTruckId
    || normalizeOptionalString(endOfDayLoadRemovalReferenceOrder?.end_of_day_load_target_truck_id)
    || "";
  // Camions et remorques indépendants : le retrait nomme la remorque du Chargé PM, sinon le camion.
  const endOfDayLoadRemovalSourceTrailerName = normalizeOptionalString(endOfDayLoadRemovalReferenceOrder?.end_of_day_load_source_trailer_name);
  const endOfDayLoadRemovalSourceTruckLabel = endOfDayLoadRemovalSourceTrailerName
    || normalizeOptionalString(endOfDayLoadRemovalReferenceOrder?.end_of_day_load_source_truck_name)
    || resolveTruckLabelFromRows(endOfDayLoadTruckRows, endOfDayLoadRemovalSourceTruckId);
  const endOfDayLoadRemovalTargetTruckLabel = normalizeOptionalString(endOfDayLoadRemovalReferenceOrder?.end_of_day_load_target_trailer_name)
    || normalizeOptionalString(endOfDayLoadRemovalReferenceOrder?.end_of_day_load_target_truck_name)
    || resolveTruckLabelFromRows(endOfDayLoadTruckRows, endOfDayLoadRemovalTargetTruckId);
  const endOfDayLoadRemovalSourceDispatchDate = endOfDayLoadGroupTarget?.sourceDispatchDate
    || endOfDayLoadGroupTarget?.dispatchDate
    || normalizeOptionalString(endOfDayLoadOrderTarget?.end_of_day_load_source_dispatch_date)
    || toDateInputValue(endOfDayLoadOrderTarget?.delivery_date)
    || browserDeliveryDate;
  const endOfDayLoadSelectedSourceDispatchDate = normalizeOptionalString(endOfDayLoadSourceDispatchDate) || "";
  // Comme dans le Dispatch : une commande déjà sur un camion est chargée en PM par ce camion.
  const isEndOfDayLoadMarkerDialog = Boolean(
    normalizeOptionalString(endOfDayLoadOrderTarget?.end_of_day_load_marker_key) || endOfDayLoadGroupTarget?.markerKey,
  );
  const endOfDayLoadCarryingTruckId = !isEndOfDayLoadMarkerDialog
    ? (endOfDayLoadGroupTarget?.orderIds || [endOfDayLoadOrderTarget?.order_id])
      .map((orderId) => endOfDayLoadSourceTrucksState.orderTruckIds?.[orderId])
      .find(Boolean) || ""
    : "";
  const isEndOfDayLoadSourceTruckImposed = isEndOfDayLoadMarkerDialog || Boolean(endOfDayLoadCarryingTruckId);
  const endOfDayLoadSelectedSourceTruckId = endOfDayLoadCarryingTruckId || normalizeOptionalString(endOfDayLoadSourceTruckId) || "";
  const filterEndOfDayLoadSourceTruckOptions = (sourceProductLabel) => (
    endOfDayLoadSourceTrucksState.dispatchDate === endOfDayLoadSelectedSourceDispatchDate
      ? filterEndOfDayLoadSourceTrucks(
        isEndOfDayLoadSourceTruckImposed
          ? endOfDayLoadSourceTrucksState.options
          : filterEndOfDayLoadTargetTrucks(endOfDayLoadSourceTrucksState.options, endOfDayLoadTargetFilters),
        { keepTruckId: endOfDayLoadSelectedSourceTruckId, sourceProductLabel },
      )
      : []
  );
  const endOfDayLoadSourceTruckOptions = filterEndOfDayLoadSourceTruckOptions(endOfDayLoadSourceProductLabel);
  const isEndOfDayLoadSourceTrucksLoading = Boolean(
    endOfDayLoadSelectedSourceDispatchDate
    && (
      endOfDayLoadSourceTrucksState.dispatchDate !== endOfDayLoadSelectedSourceDispatchDate
      || endOfDayLoadSourceTrucksState.isLoading
    )
  );
  const endOfDayLoadSourceTrucksErrorMessage = endOfDayLoadSourceTrucksState.dispatchDate === endOfDayLoadSelectedSourceDispatchDate
    ? endOfDayLoadSourceTrucksState.errorMessage
    : "";
  const selectedEndOfDayLoadSourceTruck = endOfDayLoadSourceTruckOptions
    .find((truck) => truck.truckId === endOfDayLoadSelectedSourceTruckId) || null;
  const isEndOfDayLoadSourceTruckUnavailable = Boolean(
    endOfDayLoadSelectedSourceTruckId
    && !isEndOfDayLoadSourceTrucksLoading
    && !selectedEndOfDayLoadSourceTruck,
  );
  const endOfDayLoadDialogSourceTruck = selectedEndOfDayLoadSourceTruck || (endOfDayLoadSelectedSourceTruckId ? {
    truckId: endOfDayLoadSelectedSourceTruckId,
    truckName: endOfDayLoadRemovalSourceTruckLabel || endOfDayLoadSelectedSourceTruckId,
  } : null);
  const endOfDayLoadSourceTruckPolicyMessage = endOfDayLoadSourceTrucksErrorMessage
    ? ""
    : resolveEndOfDayLoadSourceTruckPolicyMessage({
      dispatchDate: endOfDayLoadSelectedSourceDispatchDate,
      dispatchState: endOfDayLoadSourceTrucksState,
      truck: selectedEndOfDayLoadSourceTruck,
      truckId: endOfDayLoadSelectedSourceTruckId,
    });
  const endOfDayLoadSourceFieldLabel = resolveEndOfDayLoadTrailerFieldLabel(endOfDayLoadDialogSourceTruck, "source");
  const normalizedEndOfDayLoadDeliveryMode = normalizeEndOfDayLoadDeliveryMode(endOfDayLoadDeliveryMode);
  const isEndOfDayLoadEveningMode = normalizedEndOfDayLoadDeliveryMode === "evening";
  const normalizedEndOfDayLoadTargetDispatchDate = normalizeOptionalString(endOfDayLoadTargetDispatchDate);
  const endOfDayLoadSourceValidationMessage = !endOfDayLoadSelectedSourceDispatchDate
    ? "Date source obligatoire."
    : !endOfDayLoadSelectedSourceTruckId
      ? `${endOfDayLoadSourceFieldLabel} obligatoire.`
      : isEndOfDayLoadSourceTruckUnavailable && !endOfDayLoadSourceTrucksErrorMessage
        ? `${endOfDayLoadSourceFieldLabel} indisponible dans ce dispatch Jour.`
        : endOfDayLoadSourceTruckPolicyMessage;
  const endOfDayLoadDateValidationMessage = !normalizedEndOfDayLoadTargetDispatchDate
    ? "Date cible obligatoire."
    : isEndOfDayLoadEveningMode && normalizedEndOfDayLoadTargetDispatchDate !== endOfDayLoadSelectedSourceDispatchDate
      ? "En mode Livrer soir, la date cible doit être identique à la date source."
      : "";
  // En mode soir, la date cible vaut la date source : le mode jour reprend sa propre date (au moins le lendemain).
  const endOfDayLoadDayTargetDispatchDate = isEndOfDayLoadEveningMode
    ? resolveEndOfDayLoadTargetDispatchDateForMode(
      endOfDayLoadSelectedSourceDispatchDate,
      "next_day",
      endOfDayLoadDayTargetDispatchDateRef.current,
    )
    : endOfDayLoadTargetDispatchDate;
  const endOfDayLoadDayOptionLabel = getEndOfDayLoadModeLongLabel("next_day", {
    sourceDispatchDate: endOfDayLoadSelectedSourceDispatchDate,
    targetDispatchDate: endOfDayLoadDayTargetDispatchDate,
  });
  const isExistingEndOfDayLoadDialog = Boolean(
    (endOfDayLoadOrderTarget && isOrderEndOfDayLoadLocked(endOfDayLoadOrderTarget))
    || endOfDayLoadGroupRemovalOrders.some((order) => isOrderEndOfDayLoadLocked(order)),
  );
  const endOfDayLoadCurrentReferenceOrder = endOfDayLoadGroupRemovalOrders[0] || endOfDayLoadOrderTarget || null;
  const endOfDayLoadCurrentSourceDispatchDate = endOfDayLoadGroupTarget?.sourceDispatchDate
    || endOfDayLoadGroupTarget?.dispatchDate
    || normalizeOptionalString(endOfDayLoadCurrentReferenceOrder?.end_of_day_load_source_dispatch_date)
    || toDateInputValue(endOfDayLoadCurrentReferenceOrder?.delivery_date)
    || browserDeliveryDate;
  const endOfDayLoadCurrentSourceTruckId = endOfDayLoadGroupTarget?.sourceTruckId
    || normalizeOptionalString(endOfDayLoadCurrentReferenceOrder?.end_of_day_load_source_truck_id)
    || "";
  const endOfDayLoadCurrentTargetDispatchDate = endOfDayLoadGroupTarget?.targetDispatchDate
    || normalizeOptionalString(endOfDayLoadCurrentReferenceOrder?.end_of_day_load_target_dispatch_date)
    || endOfDayLoadTargetDispatchDate;
  const endOfDayLoadCurrentTargetTruckId = endOfDayLoadGroupTarget?.targetTruckId
    || normalizeOptionalString(endOfDayLoadCurrentReferenceOrder?.end_of_day_load_target_truck_id)
    || "";
  const endOfDayLoadCurrentDeliveryMode = isExistingEndOfDayLoadDialog
    ? resolveEndOfDayLoadDeliveryModeFromRecord(endOfDayLoadCurrentReferenceOrder, endOfDayLoadCurrentSourceDispatchDate)
    : normalizedEndOfDayLoadDeliveryMode;
  const isEndOfDayLoadAssignmentUnchanged = Boolean(
    isExistingEndOfDayLoadDialog
    && endOfDayLoadSelectedSourceDispatchDate === endOfDayLoadCurrentSourceDispatchDate
    && endOfDayLoadSelectedSourceTruckId === endOfDayLoadCurrentSourceTruckId
    && normalizedEndOfDayLoadTargetDispatchDate === normalizeOptionalString(endOfDayLoadCurrentTargetDispatchDate)
    && endOfDayLoadSelectedSourceTruckId === endOfDayLoadCurrentTargetTruckId
    && normalizedEndOfDayLoadDeliveryMode === endOfDayLoadCurrentDeliveryMode,
  );

  function handleEndOfDayLoadDeliveryModeChange(nextMode) {
    const deliveryMode = normalizeEndOfDayLoadDeliveryMode(nextMode);
    if (deliveryMode === "evening" && normalizedEndOfDayLoadDeliveryMode !== "evening") {
      endOfDayLoadDayTargetDispatchDateRef.current = endOfDayLoadTargetDispatchDate;
    }
    setEndOfDayLoadDeliveryMode(deliveryMode);
    if (deliveryMode === "evening") {
      setEndOfDayLoadTargetDispatchDate(endOfDayLoadSelectedSourceDispatchDate);
    } else if (normalizedEndOfDayLoadDeliveryMode === "evening") {
      setEndOfDayLoadTargetDispatchDate(endOfDayLoadDayTargetDispatchDate);
    }
  }

  function handleEndOfDayLoadSourceDispatchDateChange(nextSourceDispatchDate) {
    const normalizedSourceDispatchDate = normalizeOptionalString(nextSourceDispatchDate) || "";
    setEndOfDayLoadSourceDispatchDate(normalizedSourceDispatchDate);
    if (normalizedEndOfDayLoadDeliveryMode === "evening") {
      setEndOfDayLoadTargetDispatchDate(normalizedSourceDispatchDate);
    }
  }

  function handleEndOfDayLoadTargetDispatchDateChange(nextTargetDispatchDate) {
    const normalizedTargetDispatchDate = normalizeOptionalString(nextTargetDispatchDate) || "";
    setEndOfDayLoadTargetDispatchDate(normalizedTargetDispatchDate);
    if (normalizedEndOfDayLoadDeliveryMode !== "evening") {
      endOfDayLoadDayTargetDispatchDateRef.current = normalizedTargetDispatchDate;
    }
  }

  const endOfDayLoadDialogConfig = {
    isOpen: Boolean(endOfDayLoadOrderTarget || endOfDayLoadGroupTarget),
    isPending: Boolean(isEndOfDayLoadPending),
    isRemoveConfirmOpen: isEndOfDayLoadRemoveConfirmOpen,
    isRemovePending: Boolean(isEndOfDayLoadRemovePending),
    canRemove: canRemoveEndOfDayLoad,
    onClose: handleCloseEndOfDayLoadDialog,
    onConfirm: handleConfirmEndOfDayLoadDialog,
    onOpenRemoveConfirm: handleOpenEndOfDayLoadRemoveConfirm,
    onCancelRemoveConfirm: handleCancelEndOfDayLoadRemoveConfirm,
    onConfirmRemove: handleConfirmEndOfDayLoadRemoveDialog,
    order: endOfDayLoadOrderTarget,
    deliveryMode: normalizedEndOfDayLoadDeliveryMode,
    dayOptionLabel: endOfDayLoadDayOptionLabel,
    surfaceClassName: getSegmentHoverSurfaceClassName("loading", false, isExistingEndOfDayLoadDialog, endOfDayLoadCurrentDeliveryMode),
    selectedSourceTruck: endOfDayLoadDialogSourceTruck,
    sourceFieldLabel: endOfDayLoadSourceFieldLabel,
    sourceTrailerLabel: resolveEndOfDayLoadTrailerLabel(endOfDayLoadDialogSourceTruck),
    selectedSourceTruckId: endOfDayLoadSelectedSourceTruckId,
    targetFieldLabel: resolveEndOfDayLoadTrailerFieldLabel(endOfDayLoadDialogSourceTruck),
    targetTrailerLabel: resolveEndOfDayLoadTrailerLabel(endOfDayLoadDialogSourceTruck),
    selectedSourceDispatchDate: endOfDayLoadSelectedSourceDispatchDate,
    selectedTargetDispatchDate: endOfDayLoadTargetDispatchDate,
    sourceDateValidationMessage: endOfDayLoadSourceValidationMessage,
    sourceTrucksErrorMessage: endOfDayLoadSourceTrucksErrorMessage,
    isSourceTrucksLoading: isEndOfDayLoadSourceTrucksLoading,
    isSourceTruckUnavailable: isEndOfDayLoadSourceTruckUnavailable,
    isAssignmentUnchanged: isEndOfDayLoadAssignmentUnchanged,
    targetDateValidationMessage: endOfDayLoadDateValidationMessage,
    removalSourceDispatchDate: endOfDayLoadRemovalSourceDispatchDate,
    removalSourceFieldLabel: endOfDayLoadRemovalSourceTrailerName ? "Remorque source" : "Camion source",
    removalSourceTruckLabel: endOfDayLoadRemovalSourceTruckLabel,
    removalTargetTruckLabel: endOfDayLoadRemovalTargetTruckLabel,
    subtitle: endOfDayLoadGroupTarget?.label || endOfDayLoadOrderTarget?.end_of_day_load_loading_label || "",
    title: endOfDayLoadGroupTarget ? "Groupe de chargement sélectionné" : (endOfDayLoadOrderTarget?.order_id || "Commande ciblée"),
    sourceTruckOptions: endOfDayLoadSourceTruckOptions,
    isSourceTruckImposed: isEndOfDayLoadSourceTruckImposed,
    emptySourceTrucksMessage: endOfDayLoadSourceTruckOptions.length
      ? ""
      : buildEndOfDayLoadTargetTrucksEmptyMessage(endOfDayLoadSourceProductLabel, {
        countWithoutProduct: filterEndOfDayLoadSourceTruckOptions("").length,
      }),
    groupOrderCount: endOfDayLoadGroupTarget?.orderIds?.length || 0,
    groupOrderPreview: endOfDayLoadGroupOrderPreview,
    onDeliveryModeChange: handleEndOfDayLoadDeliveryModeChange,
    onSourceDispatchDateChange: handleEndOfDayLoadSourceDispatchDateChange,
    onSourceTruckChange: setEndOfDayLoadSourceTruckId,
    onTargetDispatchDateChange: handleEndOfDayLoadTargetDispatchDateChange,
  };
  const endOfDayLoadProcessingSubject = endOfDayLoadDialogConfig.groupOrderCount > 1
    ? `Groupe de ${endOfDayLoadDialogConfig.groupOrderCount} commandes${endOfDayLoadDialogConfig.groupOrderPreview ? ` · ${endOfDayLoadDialogConfig.groupOrderPreview}` : ""}`
    : (endOfDayLoadOrderTarget?.order_id || "Commande PM");
  const endOfDayLoadProcessingNotice = isEndOfDayLoadPending
    ? {
        title: "Assignation PM en cours",
        message: `${endOfDayLoadProcessingSubject} · mise à jour du planning.`,
      }
    : isEndOfDayLoadRemovePending
      ? {
          title: "Retrait PM en cours",
          message: `${endOfDayLoadProcessingSubject} · mise à jour du planning.`,
        }
      : null;

  const orderConfirmationDialogProps = {
    clearDeliveredDialog: clearDeliveredDialogConfig,
    createOrderDialog: createOrderDialogConfig,
    deleteSelectionDialog: deleteSelectionDialogConfig,
  };

  const emptyStateMessage = isPendingBillingApprovalFilter
    ? "Aucune commande livrée n’est actuellement en attente d’approbation."
    : totalOrderCount === 0
      ? "Aucune commande n’est encore disponible dans le Data Manager pour la date chargée."
    : isPmOnlyFilter
      ? activeSearchQuery
        ? `Aucune commande Chargé PM ne correspond à la recherche « ${activeSearchQuery} ».`
        : "Aucune commande Chargé PM dans la sélection courante."
    : isBrowseMode && orderBrowseDateFilter && activeSearchQuery
      ? `Aucune commande ne correspond à « ${activeSearchQuery} » pour le ${formatDateDisplay(orderBrowseDateFilter)}.`
      : isBrowseMode && orderBrowseDateFilter
        ? `Aucune commande trouvée pour le ${formatDateDisplay(orderBrowseDateFilter)}.`
        : isBrowseMode && activeSearchQuery
          ? `Aucune commande ne correspond à la recherche « ${activeSearchQuery} » dans l’ensemble des commandes.`
          : isBrowseMode
            ? "Aucune commande récente à afficher."
            : activeSearchQuery
              ? `Aucune commande ne correspond à la recherche « ${activeSearchQuery} ». Essayez un n° de commande, un client ou un silo.`
              : currentDispatchOrderCount > 0
                ? "Aucune commande visible dans la sélection courante."
                : "Aucune commande n’est actuellement chargée dans le dispatch. Utilisez le filtre secondaire pour cibler une date ou une commande.";

  const coopClientNames = buildCoopClientNames(clientCorrespondences);

  return (
    <BillToCompaniesContext.Provider value={coopClientNames}>
    <div ref={rootRef} className={`data-manager-viewport-panel grid ${isCreatingOrder ? "gap-1.5" : "gap-1.5"}`}>
      {localDispatchSaveNotice ? (
        <div className={buildLocalDispatchSaveNoticeClassName(localDispatchSaveNotice.tone)} aria-live="polite">
          {localDispatchSaveNotice.message}
        </div>
      ) : null}

      <DataManagerOrdersCreatePanel {...createOrderPanelProps} />

      <DataManagerOrdersGrid
        productCatalog={productCatalog}
        allVisibleDeletableOrdersSelected={allVisibleDeletableOrdersSelected}
        allVisibleSelectableOrdersSelected={allVisibleSelectableOrdersSelected}
        addressValidationErrors={addressValidationErrors}
        addressValidationPendingKeys={addressValidationPendingKeys}
        canApplyEndOfDayLoadGroup={selectedEndOfDayLoadGroupAction.canApply}
        bulkBillingApprovalSelectionMeta={bulkBillingApprovalSelectionMeta}
        canOpenConsolidation={canOpenConsolidation}
        canClearDeliveredOrders={canClearDeliveredOrders}
        canDeleteDeliveredOrders={canDeleteDeliveredOrders}
        canEditOrders={canEditOrders}
        canEditOrderExecution={canWriteOrderActualTonnage || canMarkOrdersDelivered || canWriteOrderDeliveryDriver}
        canAssignEndOfDayLoad={typeof assignOrdersEndOfDayLoad === "function"}
        deletableVisibleOrders={deletableVisibleOrders}
        endOfDayLoadGroupReason={selectedEndOfDayLoadGroupAction.reason}
        editingOrderFocusKey={editingOrderFocusKey}
        editingOrderId={editingOrderId}
        emptyStateMessage={emptyStateMessage}
        isBrowseMode={isBrowseMode}
        isCreatingOrder={isCreatingOrder}
        isEndOfDayLoadPending={isEndOfDayLoadPending}
        isDeletingSelection={isDeletingSelection}
        isBulkBillingApprovalPending={false}
        isGlobalSearchMode={isGlobalSearchMode}
        isPendingBillingApprovalFilter={isPendingBillingApprovalFilter}
        isPending={isPending}
        loadingClientOptions={loadingClientOptions}
        loadingClientOptionsByLookupKey={loadingClientOptionsByLookupKey}
        deliveryDatePickerValue={deliveryDatePickerValue}
        orderDefaultFocusKey={ORDER_DEFAULT_FOCUS_KEY}
        orderDrafts={orderDrafts}
        orderTableColumnStyles={orderTableColumnStyles}
        orderTableStyle={orderTableStyle}
        registerAddressValidationFieldRef={registerAddressValidationFieldRef}
        searchQuery={searchQuery}
        pendingBillingApprovalCount={pendingBillingApprovalCount}
        selectedDeleteCount={selectedDeleteCount}
        selectedOrderCount={selectedOrderCount}
        selectedOrderIds={selectedOrderIds}
        selectableVisibleOrders={selectableVisibleOrders}
        showEndOfDayLoadGroupControls={typeof assignOrdersEndOfDayLoad === "function"}
        showBulkDeleteControls={showBulkDeleteControls}
        showBulkBillingApprovalControls={canOpenConsolidation}
        showSelectionControls={showOrderSelectionControls}
        sortedOrders={sortedOrders}
        sortState={sortState}
        totalOrderCount={totalOrderCount}
        activeSearchQuery={activeSearchQuery}
        trimmedSearchQuery={trimmedSearchQuery}
        visibleOrderCount={visibleOrderCount}
        freezableOrderColumnDescriptors={freezableOrderColumnDescriptors}
        frozenColumnKeys={frozenColumnKeys}
        frozenColumnLabels={frozenColumnLabels}
        hasFrozenPanes={hasFrozenPanes}
        isFrozenColumn={isFrozenColumn}
        onToggleColumnFreeze={toggleColumnFreeze}
        onUnfreezePanes={unfreezePanes}
        {...orderGridInteractionProps}
      />

      {isOrderBrowserOpen ? (
        <DataManagerOrdersBrowserDialog {...orderBrowserDialogProps} />
      ) : null}

      {isPdfDialogOpen ? (
        <DataManagerOrdersPdfDialog {...orderPdfDialogProps} />
      ) : null}

      <DataManagerOrdersConfirmationDialogs {...orderConfirmationDialogProps} />

      {orderExecutionTarget && !isOrderBillingApproved(orderExecutionTarget) ? (
        <DataManagerOrderExecutionDialog
          canMarkDelivered={canMarkOrdersDelivered && !orderExecutionTarget?.is_cancelled}
          canWriteDeliveryDriver={canWriteOrderDeliveryDriver && !orderExecutionTarget?.is_cancelled}
          canWriteActualTonnage={canWriteOrderActualTonnage && !orderExecutionTarget?.is_cancelled}
          driverOptions={deliveryDriverOptions}
          errorMessage={orderExecutionErrorMessage || orderExecutionConflict}
          isPending={Boolean(orderExecutionPending)}
          onClose={handleCloseOrderExecutionDialog}
          onSubmit={handleSubmitOrderExecution}
          order={orderExecutionTarget}
        />
      ) : null}

      {endOfDayLoadDialogConfig.isOpen && !endOfDayLoadDialogConfig.isRemoveConfirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/28 px-4 py-6 backdrop-blur-xs">
          <section role="dialog" aria-modal="true" aria-label={endOfDayLoadDialogConfig.title} className={["grid max-h-[calc(100vh-3rem)] w-full max-w-md gap-2.5 overflow-y-auto rounded-[4px] border p-3 shadow-[0_24px_60px_rgba(15,23,42,0.22)]!", endOfDayLoadDialogConfig.surfaceClassName].join(" ")}>
            <div className="grid gap-1">
              <p className="m-0 text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-content-copy">Chargé en PM</p>
              <h3 className="m-0 text-base font-semibold text-slate-950">{endOfDayLoadDialogConfig.title}</h3>
              {endOfDayLoadDialogConfig.subtitle ? (
                <p className="m-0 text-xs font-semibold leading-5 text-slate-500">{endOfDayLoadDialogConfig.subtitle}</p>
              ) : null}
              {endOfDayLoadDialogConfig.groupOrderCount > 1 ? (
                <p className="m-0 rounded-[4px] border border-[rgba(15,23,42,0.1)] bg-white px-3 py-2 text-xs font-semibold leading-5 text-slate-700">
                  Groupe complet à assigner en Chargé PM · {endOfDayLoadDialogConfig.groupOrderCount} commandes{endOfDayLoadDialogConfig.groupOrderPreview ? ` · ${endOfDayLoadDialogConfig.groupOrderPreview}` : ""}
                </p>
              ) : null}
              <p className="m-0 flex flex-wrap items-center gap-1.5 text-sm font-semibold leading-5 text-slate-600">
                <span>{endOfDayLoadDialogConfig.sourceTrailerLabel || endOfDayLoadDialogConfig.sourceFieldLabel}</span>
                <span className="text-slate-400">·</span>
                <span>{endOfDayLoadDialogConfig.selectedSourceDispatchDate || "Date source"}</span>
                <span className="text-slate-400">→</span>
                <span>{endOfDayLoadDialogConfig.targetTrailerLabel || endOfDayLoadDialogConfig.targetFieldLabel}</span>
                <span className="text-slate-400">·</span>
                <span>{endOfDayLoadDialogConfig.selectedTargetDispatchDate || "Date cible"}</span>
              </p>
            </div>
            <div className="grid gap-2 rounded-[4px] border border-[rgba(15,23,42,0.1)] bg-white px-3 py-3">
            <div className="grid gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>Mode de livraison</span>
              <div className="grid grid-cols-2 gap-1 rounded-[4px] border border-[rgba(148,163,184,0.22)] bg-white p-1">
                {[
                  { icon: Sun, label: endOfDayLoadDialogConfig.dayOptionLabel || "Livrer demain", value: "next_day" },
                  { icon: Moon, label: "Livrer soir", value: "evening" },
                ].map((option) => {
                  const isActive = endOfDayLoadDialogConfig.deliveryMode === option.value;
                  const OptionIcon = option.icon;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={[
                        "inline-flex min-h-8 min-w-0 items-center justify-center gap-1 rounded-[4px] border px-2 py-1 text-[0.7rem] font-extrabold transition-colors duration-150 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent",
                        isActive
                          ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                          : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                        "disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400",
                      ].join(" ")}
                      aria-pressed={isActive}
                      disabled={endOfDayLoadDialogConfig.isPending}
                      onClick={() => endOfDayLoadDialogConfig.onDeliveryModeChange(option.value)}
                    >
                      <OptionIcon className="h-3.5 w-3.5" aria-hidden="true" strokeWidth={2.2} />
                      <span>{option.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(100%,10rem),1fr))] gap-2">
            <div className="grid min-w-0 content-start gap-2">
            <label className="grid min-w-0 gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>Date source</span>
              <input
                className="h-9 w-full min-w-0 rounded-[4px] border border-slate-300 bg-white px-2 text-[0.82rem] text-slate-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                disabled={endOfDayLoadDialogConfig.isPending}
                type="date"
                value={endOfDayLoadDialogConfig.selectedSourceDispatchDate}
                onChange={(event) => endOfDayLoadDialogConfig.onSourceDispatchDateChange(event.target.value)}
              />
            </label>
            <label className="grid min-w-0 gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>{endOfDayLoadDialogConfig.sourceFieldLabel}</span>
              <select
                className="h-9 w-full min-w-0 rounded-[4px] border border-slate-300 bg-white px-2 text-[0.82rem] text-slate-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                disabled={endOfDayLoadDialogConfig.isPending || endOfDayLoadDialogConfig.isSourceTruckImposed || endOfDayLoadDialogConfig.isSourceTrucksLoading || Boolean(endOfDayLoadDialogConfig.sourceTrucksErrorMessage) || endOfDayLoadDialogConfig.sourceTruckOptions.length === 0}
                value={endOfDayLoadDialogConfig.selectedSourceTruckId}
                onChange={(event) => endOfDayLoadDialogConfig.onSourceTruckChange(event.target.value)}
              >
                <option value="" disabled>
                  {endOfDayLoadDialogConfig.isSourceTrucksLoading ? "Chargement des remorques..." : "Sélectionner une remorque source"}
                </option>
                {endOfDayLoadDialogConfig.isSourceTruckUnavailable && endOfDayLoadDialogConfig.selectedSourceTruckId ? (
                  <option value={endOfDayLoadDialogConfig.selectedSourceTruckId} disabled>
                    {endOfDayLoadDialogConfig.sourceTrailerLabel || endOfDayLoadDialogConfig.selectedSourceTruckId} (indisponible)
                  </option>
                ) : null}
                {endOfDayLoadDialogConfig.sourceTruckOptions.map((truck) => (
                  <option key={truck.truckId} value={truck.truckId}>
                    {resolveEndOfDayLoadTrailerLabel(truck)}
                  </option>
                ))}
              </select>
            </label>
            </div>
            <div className="grid min-w-0 content-start gap-2">
            <label className="grid min-w-0 gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>Date cible</span>
              <input
                className="h-9 w-full min-w-0 rounded-[4px] border border-slate-300 bg-white px-2 text-[0.82rem] text-slate-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                disabled={endOfDayLoadDialogConfig.isPending || endOfDayLoadDialogConfig.deliveryMode === "evening"}
                type="date"
                value={endOfDayLoadDialogConfig.selectedTargetDispatchDate}
                onChange={(event) => endOfDayLoadDialogConfig.onTargetDispatchDateChange(event.target.value)}
              />
            </label>
            <label className="grid min-w-0 gap-1 text-[0.7rem] font-semibold text-content-copy">
              <span>{endOfDayLoadDialogConfig.targetFieldLabel}</span>
              <input
                className="h-9 w-full min-w-0 rounded-[4px] border border-slate-300 bg-white px-2 text-[0.82rem] text-slate-900 outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20 disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100"
                disabled
                placeholder="Suit la source"
                type="text"
                value={endOfDayLoadDialogConfig.targetTrailerLabel}
              />
            </label>
            </div>
            </div>
            {endOfDayLoadDialogConfig.isSourceTrucksLoading ? (
              <p className="m-0 inline-flex items-center gap-2 rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-600">
                <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
                Chargement du dispatch source...
              </p>
            ) : null}
            {endOfDayLoadDialogConfig.sourceTrucksErrorMessage ? (
              <p className="m-0 text-xs font-semibold leading-5 text-rose-700">
                {endOfDayLoadDialogConfig.sourceTrucksErrorMessage}
              </p>
            ) : null}
            {!endOfDayLoadDialogConfig.isSourceTrucksLoading && !endOfDayLoadDialogConfig.sourceTrucksErrorMessage && endOfDayLoadDialogConfig.selectedSourceDispatchDate && endOfDayLoadDialogConfig.sourceTruckOptions.length === 0 ? (
              <p className="m-0 rounded-[4px] border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-xs font-semibold text-amber-800">
                {endOfDayLoadDialogConfig.emptySourceTrucksMessage}
              </p>
            ) : null}
            <p className="m-0 rounded-[4px] border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-semibold leading-5 text-slate-600">
              {[endOfDayLoadDialogConfig.selectedSourceDispatchDate ? `Source ${endOfDayLoadDialogConfig.selectedSourceDispatchDate}` : "", endOfDayLoadDialogConfig.selectedTargetDispatchDate ? `Cible ${endOfDayLoadDialogConfig.selectedTargetDispatchDate}` : ""].filter(Boolean).join(" · ")}
            </p>
            {endOfDayLoadDialogConfig.targetDateValidationMessage ? (
              <p className="m-0 text-xs font-semibold leading-5 text-rose-700">
                {endOfDayLoadDialogConfig.targetDateValidationMessage}
              </p>
            ) : null}
            {endOfDayLoadDialogConfig.sourceDateValidationMessage ? (
              <p className="m-0 text-xs font-semibold leading-5 text-rose-700">
                {endOfDayLoadDialogConfig.sourceDateValidationMessage}
              </p>
            ) : null}
            {endOfDayLoadDialogConfig.isAssignmentUnchanged ? (
              <p className="m-0 rounded-[4px] border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-xs font-semibold text-slate-600">
                Aucun changement à appliquer.
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                className="inline-flex h-9 items-center rounded-[4px] border px-3 text-[0.72rem] font-semibold transition disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                disabled={endOfDayLoadDialogConfig.isPending || endOfDayLoadDialogConfig.isRemovePending || endOfDayLoadDialogConfig.isSourceTrucksLoading || Boolean(endOfDayLoadDialogConfig.sourceTrucksErrorMessage) || !endOfDayLoadDialogConfig.selectedSourceDispatchDate || !endOfDayLoadDialogConfig.selectedSourceTruckId || !endOfDayLoadDialogConfig.selectedTargetDispatchDate || Boolean(endOfDayLoadDialogConfig.sourceDateValidationMessage) || Boolean(endOfDayLoadDialogConfig.targetDateValidationMessage) || endOfDayLoadDialogConfig.isAssignmentUnchanged}
                onClick={endOfDayLoadDialogConfig.onConfirm}
              >
                {endOfDayLoadDialogConfig.isPending ? "Assignation..." : "Assigner"}
              </button>
              <button
                type="button"
                className="inline-flex h-9 items-center rounded-[4px] border px-3 text-[0.72rem] font-semibold transition disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"
                disabled={endOfDayLoadDialogConfig.isPending || endOfDayLoadDialogConfig.isRemovePending}
                onClick={endOfDayLoadDialogConfig.onClose}
              >
                Fermer
              </button>
              {endOfDayLoadDialogConfig.canRemove ? (
                <button
                  type="button"
                  className="ml-auto inline-flex h-9 items-center rounded-[4px] border px-3 text-[0.72rem] font-semibold transition disabled:cursor-not-allowed disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 border-rose-200 bg-rose-50 text-rose-800 hover:bg-rose-100"
                  disabled={endOfDayLoadDialogConfig.isPending || endOfDayLoadDialogConfig.isRemovePending}
                  onClick={endOfDayLoadDialogConfig.onOpenRemoveConfirm}
                >
                  Retirer PM
                </button>
              ) : null}
            </div>
            </div>
          </section>
        </div>
      ) : null}

      {endOfDayLoadDialogConfig.isOpen && endOfDayLoadDialogConfig.isRemoveConfirmOpen ? (
        <EndOfDayLoadRemovalDialog
          groupOrderCount={endOfDayLoadDialogConfig.groupOrderCount}
          groupOrderPreview={endOfDayLoadDialogConfig.groupOrderPreview}
          isPending={endOfDayLoadDialogConfig.isRemovePending}
          onCancel={endOfDayLoadDialogConfig.onCancelRemoveConfirm}
          onClose={endOfDayLoadDialogConfig.onCancelRemoveConfirm}
          onConfirm={endOfDayLoadDialogConfig.onConfirmRemove}
          sourceDate={endOfDayLoadDialogConfig.removalSourceDispatchDate}
          sourceFieldLabel={endOfDayLoadDialogConfig.removalSourceFieldLabel}
          sourceTruckLabel={endOfDayLoadDialogConfig.removalSourceTruckLabel}
          subtitle={endOfDayLoadDialogConfig.subtitle}
          targetDate={endOfDayLoadDialogConfig.selectedTargetDispatchDate}
          targetTruckLabel={endOfDayLoadDialogConfig.removalTargetTruckLabel}
          title={endOfDayLoadDialogConfig.groupOrderCount > 1 ? "Retirer le PM du groupe ?" : "Retirer le PM ?"}
        />
      ) : null}

      {endOfDayLoadProcessingNotice ? (
        <div className="fixed bottom-4 right-4 z-[70] flex w-[min(24rem,calc(100vw-2rem))] items-start gap-3 rounded-lg border border-emerald-200 bg-white px-4 py-3 text-sm shadow-[0_18px_45px_rgba(15,23,42,0.2)]">
          <LoaderCircle className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-emerald-700" aria-hidden="true" />
          <div className="grid gap-0.5">
            <p className="m-0 text-sm font-semibold text-slate-950">{endOfDayLoadProcessingNotice.title}</p>
            <p className="m-0 text-xs font-semibold leading-5 text-slate-600">{endOfDayLoadProcessingNotice.message}</p>
          </div>
        </div>
      ) : null}

      {missingCivicNumberDialog}

    </div>
    </BillToCompaniesContext.Provider>
  );
}
